import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { APP_VERSION, APP_BUILD_DATETIME } from './version';
import {
  DownloadCloud,
  ChevronDown,
  Search,
  Filter,
  ArrowUpDown,
  LayoutGrid,
  Plus,
  Mail,
  RefreshCw,
  TrendingUp,
  LayoutDashboard,
  Users,
  BarChart2,
  Trash2,
  ExternalLink,
  Sun,
  Moon,
  Kanban,
  LayoutList,
  Check,
  Send,
  X,
  Palette,
  Globe,
  ShieldCheck,
  ShieldAlert,
  Layers,
  LogOut,
  Archive,
  Sparkles,
  Table,
  Menu,
  ChevronLeft,
  User,
  UserCheck,
  UserX,
  UserCog,
  BookOpen,
  HelpCircle,
  Megaphone
} from 'lucide-react';
import { signInWithPopup, signOut, onAuthStateChanged, GoogleAuthProvider } from 'firebase/auth';
import { auth, googleProvider } from './lib/firebase';
import { motion, AnimatePresence } from 'motion/react';
import { 
  fetchLeadsFromFirestore, 
  saveLeadToFirestore, 
  deleteLeadFromFirestore, 
  fetchCRMUsers, 
  saveCRMUser, 
  updateCRMUserRole, 
  getAuthHeaders,
  CRMUser 
} from './lib/db';
import Sidebar from './components/Sidebar';
import KPICards from './components/KPICards';
import UpcomingDeadlines from './components/UpcomingDeadlines';
import BoardTable from './components/BoardTable';
import KanbanBoard from './components/KanbanBoard';
import SlideOver from './components/SlideOver';
import Tooltip from './components/Tooltip';
import GoogleHub from './components/GoogleHub';
import ReauthModal from './components/ReauthModal';
import { onGoogleTokenExpired, isGoogleTokenExpired } from './lib/googleApi';
import { sortLeadsDefault, sortLeadsByField } from './lib/leadSorting';
import { resolveLeadsForNotification, resolvePrimaryLeadId } from './lib/notificationLeadResolver';
import UserMgmt from './components/UserMgmt';
import Settings from './components/Settings';
import AdminSettings from './components/AdminSettings';
import Profile from './components/Profile';
import NotificationBell, { AppNotification } from './components/NotificationBell';
import AIAdvisorModal from './components/AIAdvisorModal';
import LogisticsChatWidget from './components/LogisticsChatWidget';
import { UserRole, normalizeRole } from './lib/db';
import LeadCaptureForm from './components/LeadCaptureForm';
import PublicFormRunner from './features/onlineForms/PublicFormRunner';
import { sendGmailEmail } from './lib/googleApi';
import { createFollowUpEvent } from './lib/googleWorkspace';
import NonFloral from './components/NonFloral';
import CustomerMaster from './components/CustomerMaster';
import ConsigneeShipperSearch from './components/ConsigneeShipperSearch';
import AelisLogisticsHub from './components/AelisLogisticsHub';
import FormsHub, { FormsSubTab } from './components/FormsHub';
import ColumnCustomizerModal, { ColumnSetting, DEFAULT_COLUMNS, normalizeColumnPreferences } from './components/ColumnCustomizerModal';
import QuickFiltersBar from './components/QuickFiltersBar';
import AdvancedFilterModal from './components/AdvancedFilterModal';
import MultiSortModal from './components/MultiSortModal';
import SmartViewSelector from './components/SmartViewSelector';
import ArchiveRecoveryModal from './components/ArchiveRecoveryModal';
import { fetchUserPreferences, saveUserPreferences } from './lib/db';
import { getLeadLastUpdateDate, getLeadUpdateGroup, UPDATE_GROUP_ORDER } from './lib/dateUtils';


import HelpCenter from './components/HelpCenter';
import BannerBar from './components/BannerBar';
import FeatureShowcaseModal from './components/FeatureShowcaseModal';
import {
  fetchAnnouncements,
  saveAnnouncement,
  deleteAnnouncementApi,
  dismissAnnouncementApi,
  getLocalDismissedIds,
} from './lib/announcements';
import { bridgeFormsAuth, bridgeFormsAuthWithToken } from './lib/formsDb';
import { INITIAL_LEADS } from './data';
import {
  Lead,
  PipelineType,
  ActivityLogEntry,
  PIPELINE_BOARD_CATEGORIES,
  STATUS_DISPLAY_LABELS,
  DEFAULT_STATUS_COLORS,
  migrateStatusColors,
  resolveStatusColor,
  FilterRule,
  SortRule,
  SmartView,
  AUTHORIZED_SALESPERSONS,
  AnnouncementBanner,
  shouldUserSeeAnnouncement,
} from './types';
import { STAGES_CONFIG, StageType, getStageForPipeline } from './lib/stages';


import {
  seedActivityLog,
  getLeadChanges,
  STATUS_TO_PIPELINE,
  PIPELINE_TO_STATUS,
  BOARDS_CONFIG,
  ROLE_TABS,
} from './lib/leadActivity';

export default function App() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [activeStageTab, setActiveStageTab] = useState<StageType>('Opportunity');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const [currentUser, setCurrentUser] = useState<any>(null);
  const [dbUser, setDbUser] = useState<CRMUser | null>(null);
  const [authReady, setAuthReady] = useState(false);
  // Set when the server rejects a signed-in account (wrong domain, or not yet
  // granted access by an administrator).
  const [accessDenied, setAccessDenied] = useState<string | null>(null);
  const [nonFloralTab, setNonFloralTab] = useState<'active' | 'history' | 'lara' | 'maintenance' | 'audit'>('active');
  const [formsSubTab, setFormsSubTab] = useState<FormsSubTab>('send');
  const [isAIAdvisorOpen, setIsAIAdvisorOpen] = useState(false);


  // In-app notification center (bell in the header)
  const [notifications, setNotifications] = useState<AppNotification[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('crm_notifications') || '[]');
    } catch {
      return [];
    }
  });

  const addNotification = (title: string, body: string, leadId?: string, leadIds?: string[]) => {
    // If explicit leadId is not provided, attempt to heuristically resolve from available leads
    const effectiveLeadId =
      leadId ||
      (leadsRef.current.length > 0
        ? resolvePrimaryLeadId({ id: '', title, body, time: '', read: false }, leadsRef.current)
        : undefined);

    const item: AppNotification = {
      id: `ntf-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
      title,
      body,
      time: new Date().toISOString(),
      read: false,
      leadId: effectiveLeadId,
      leadIds,
    };
    setNotifications(prev => {
      const next = [item, ...prev].slice(0, 50);
      try { localStorage.setItem('crm_notifications', JSON.stringify(next)); } catch { /* best-effort */ }
      return next;
    });
    // Desktop push alert when the user has opted in via the bell menu
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        const notif = new Notification(title, { body });
        const targetId = effectiveLeadId;
        if (targetId) {
          notif.onclick = () => {
            window.focus();
            const targetLead = leadsRef.current.find(l => l.id === targetId) || leads.find(l => l.id === targetId);
            if (targetLead) {
              setEditingLead(targetLead);
              setIsSlideOverOpen(true);
              setActiveTab('dashboard');
            }
          };
        }
      } catch { /* ignore */ }
    }
  };

  const updateNotifications = (next: AppNotification[]) => {
    setNotifications(next);
    try { localStorage.setItem('crm_notifications', JSON.stringify(next)); } catch { /* best-effort */ }
  };

  // Lead writes surface failures now that the data layer rethrows instead of
  // silently swallowing them. The edit is still cached locally, but the user is
  // told the server copy did not save.
  const persistLead = (lead: Lead) => {
    saveLeadToFirestore(lead).catch((err: any) => {
      console.error('Failed to save lead:', err);
      addNotification(
        'Save failed — not stored on the server',
        `${lead.company || lead.name}: ${err?.message || 'Unknown error'}. Your change is cached in this browser only.`,
        lead.id
      );
    });
  };
  const [accessToken, setAccessToken] = useState<string | null>(() => {
    return localStorage.getItem('google_access_token');
  });
  const [realUserRole, setRealUserRole] = useState<UserRole>('sales');
  const [impersonatedUser, setImpersonatedUser] = useState<CRMUser | null>(() => {
    try {
      const saved = sessionStorage.getItem('armellini_impersonated_user');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [isImpersonateMenuOpen, setIsImpersonateMenuOpen] = useState(false);
  const [impersonateFilter, setImpersonateFilter] = useState('');

  // Effective role is governed by impersonation when active (double guardrail)
  const userRole: UserRole = impersonatedUser ? impersonatedUser.role : realUserRole;
  const isImpersonating = Boolean(impersonatedUser && realUserRole === 'admin');
  const canViewFinancials = userRole !== 'warehouse';

  const [crmUsersList, setCrmUsersList] = useState<CRMUser[]>([]);

  const [theme, setTheme] = useState<'light' | 'dark' | 'armellini-blue' | 'armellini-dark'>(() => {
    return (localStorage.getItem('leadtracker_theme_v3') as any) || 'light';
  });
  const [isThemeMenuOpen, setIsThemeMenuOpen] = useState(false);
  const [layoutMode, setLayoutMode] = useState<'pipeline' | 'table' | 'kanban'>(() => {
    const saved = localStorage.getItem('leadtracker_layout');
    if (saved === 'table') return 'pipeline'; // Migrate legacy table mode to pipeline view
    return (saved as 'pipeline' | 'table' | 'kanban') || 'pipeline';
  });
  const [leads, setLeads] = useState<Lead[]>([]);
  const [isLeadsLoading, setIsLeadsLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [autoRefreshCountdown, setAutoRefreshCountdown] = useState(30);

  // Dashboard filter / sort / group-by controls
  const [openMenu, setOpenMenu] = useState<null | 'filter' | 'sort' | 'group' | 'tableFilter' | 'tableSort' | 'tableGroup'>(null);
  const [filters, setFilters] = useState<{ owner: string; status: string; service: string; industry: string; state: string; lastUpdate: string; lastUpdateCustomStart: string; lastUpdateCustomEnd: string }>({ owner: '', status: '', service: '', industry: '', state: '', lastUpdate: '', lastUpdateCustomStart: '', lastUpdateCustomEnd: '' });
  const [sortField, setSortField] = useState<'default' | 'name' | 'dealValue' | 'followUpDate' | 'lastContacted' | 'lastUpdateDate' | 'status' | string>('default');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [groupBy, setGroupBy] = useState<'pipeline' | 'owner' | 'status' | 'service' | 'industry' | 'lastUpdateDate'>('pipeline');
  const activeFilterCount = Object.values(filters).filter(Boolean).length;
  const [isSlideOverOpen, setIsSlideOverOpen] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [defaultPipeline, setDefaultPipeline] = useState<PipelineType | null>(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(true);

  const leadsRef = useRef<Lead[]>([]);
  useEffect(() => {
    leadsRef.current = leads;
  }, [leads]);

  const handleSelectLeadById = useCallback((leadId: string) => {
    const targetLead = leadsRef.current.find((l) => l.id === leadId) || leads.find((l) => l.id === leadId);
    if (targetLead) {
      setEditingLead(targetLead);
      setIsSlideOverOpen(true);
      setActiveTab('dashboard');
    }
  }, [leads]);

  // Customization & FRS states
  const [columnPreferences, setColumnPreferences] = useState<ColumnSetting[]>(DEFAULT_COLUMNS);
  const [smartViews, setSmartViews] = useState<SmartView[]>([]);
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [advancedRules, setAdvancedRules] = useState<FilterRule[]>([]);
  const [multiSortRules, setMultiSortRules] = useState<SortRule[]>([]);
  const [includeArchived, setIncludeArchived] = useState<boolean>(false);

  const [isColumnCustomizerOpen, setIsColumnCustomizerOpen] = useState(false);
  const [isAdvancedFilterOpen, setIsAdvancedFilterOpen] = useState(false);
  const [isMultiSortOpen, setIsMultiSortOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isReauthModalOpen, setIsReauthModalOpen] = useState(false);

  // --- What's New & Announcement Banners State ---
  const [announcements, setAnnouncements] = useState<AnnouncementBanner[]>([]);
  const [dismissedAnnouncementIds, setDismissedAnnouncementIds] = useState<string[]>(() => getLocalDismissedIds());
  const [isWhatNewModalOpen, setIsWhatNewModalOpen] = useState(false);
  const [selectedSpotlightAnnouncement, setSelectedSpotlightAnnouncement] = useState<AnnouncementBanner | null>(null);
  const [announcementsLoadedForUid, setAnnouncementsLoadedForUid] = useState<string | null>(null);
  const autoShownAnnouncementIdsRef = useRef<Set<string>>(new Set());

  const loadAnnouncements = useCallback(async (uidForTracking?: string | null) => {
    try {
      const data = await fetchAnnouncements();
      setAnnouncements(data);
    } catch (err) {
      console.warn('Failed to load announcements:', err);
    } finally {
      if (uidForTracking) {
        setAnnouncementsLoadedForUid(uidForTracking);
      }
    }
  }, []);

  useEffect(() => {
    loadAnnouncements(currentUser?.uid || null);
  }, [currentUser?.uid, loadAnnouncements]);

  // Evaluate visible announcements matching current user
  const visibleAnnouncements = useMemo(() => {
    const user = currentUser ? {
      uid: currentUser.uid,
      role: userRole,
      email: currentUser.email,
      departmentId: (currentUser as any).departmentId,
      departmentIds: (currentUser as any).departmentIds
    } : null;

    return announcements.filter(a => shouldUserSeeAnnouncement(a, user, dismissedAnnouncementIds));
  }, [announcements, currentUser, userRole, dismissedAnnouncementIds]);

  // Top banners to display
  const topBanners = useMemo(() => {
    return visibleAnnouncements.filter(a => a.displayMode === 'top_banner' || a.displayMode === 'both');
  }, [visibleAnnouncements]);

  // Showcase modals
  const showcaseAnnouncements = useMemo(() => {
    return visibleAnnouncements.filter(a => a.displayMode === 'modal_showcase' || a.displayMode === 'both');
  }, [visibleAnnouncements]);

  // Automatically pop out the What's New modal when the user first logs in if not dismissed
  useEffect(() => {
    if (!currentUser?.uid || accessDenied) return;
    if (announcementsLoadedForUid !== currentUser.uid) return;
    if (showcaseAnnouncements.length === 0) return;

    const topShowcase = showcaseAnnouncements[0];
    const trackingKey = `${currentUser.uid}:${topShowcase.id}`;
    if (!autoShownAnnouncementIdsRef.current.has(trackingKey)) {
      autoShownAnnouncementIdsRef.current.add(trackingKey);
      setSelectedSpotlightAnnouncement(topShowcase);
      setIsWhatNewModalOpen(true);
    }
  }, [currentUser?.uid, accessDenied, announcementsLoadedForUid, showcaseAnnouncements]);

  const handleDismissBanner = (id: string) => {
    setDismissedAnnouncementIds(prev => (prev.includes(id) ? prev : [...prev, id]));
    if (currentUser?.uid) {
      setAnnouncements(prev =>
        prev.map(a =>
          a.id === id
            ? { ...a, dismissedBy: Array.from(new Set([...(a.dismissedBy || []), currentUser.uid])) }
            : a
        )
      );
    }
    dismissAnnouncementApi(id);
  };

  const handleDismissModal = (id: string, dontShowAgain: boolean) => {
    if (dontShowAgain) {
      setDismissedAnnouncementIds(prev => (prev.includes(id) ? prev : [...prev, id]));
      if (currentUser?.uid) {
        setAnnouncements(prev =>
          prev.map(a =>
            a.id === id
              ? { ...a, dismissedBy: Array.from(new Set([...(a.dismissedBy || []), currentUser.uid])) }
              : a
          )
        );
      }
      dismissAnnouncementApi(id);
    }
  };

  const handleSaveAnnouncement = async (announcement: AnnouncementBanner) => {
    await saveAnnouncement(announcement);
    await loadAnnouncements();
    addNotification('Announcement Saved', `"${announcement.title}" has been saved.`);
  };

  const handleDeleteAnnouncement = async (id: string) => {
    await deleteAnnouncementApi(id);
    await loadAnnouncements();
    addNotification('Announcement Deleted', `Announcement ${id} was deleted.`);
  };

  const handleToggleActiveAnnouncement = async (id: string, active: boolean) => {
    const target = announcements.find(a => a.id === id);
    if (target) {
      await saveAnnouncement({ ...target, isActive: active });
      await loadAnnouncements();
    }
  };


  // UAT Environment Detection State
  const [isUat, setIsUat] = useState<boolean>(() => {
    const host = window.location.hostname.toLowerCase();
    return host.includes('uat') || host.includes('staging') || host.includes('aelcrmuat');
  });

  useEffect(() => {
    fetch('/api/public/config')
      .then((res) => res.json())
      .then((data) => {
        if (data && typeof data.isUat === 'boolean') {
          setIsUat(data.isUat);
        }
      })
      .catch(() => {});
  }, []);

  // Subscribe to Google OAuth token expiration events
  useEffect(() => {
    const unsub = onGoogleTokenExpired((reason) => {
      console.warn('Google OAuth token expiration triggered reauth modal:', reason);
      setIsReauthModalOpen(true);
    });
    return unsub;
  }, []);

  // Just-in-time check: only prompt for Google re-authentication if the user
  // actively navigates to the Google Workspace Hub tab and the token is expired/missing.
  useEffect(() => {
    if (activeTab === 'google-hub') {
      if (isGoogleTokenExpired()) {
        setIsReauthModalOpen(true);
      }
    }
  }, [activeTab]);

  // Contacts View State
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
  const [showArchivedContacts, setShowArchivedContacts] = useState(false);
  const [quickEditingLead, setQuickEditingLead] = useState<Lead | null>(null);
  const [columnWidths, setColumnWidths] = useState<{ [key: string]: number }>({
    select: 45,
    company: 240,
    email: 220,
    owner: 140,
    service: 180,
    location: 130,
    value: 120,
    status: 165,
    actions: 250
  });

  const handleResizeStart = (colKey: string, e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = columnWidths[colKey] || 150;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const newWidth = Math.max(40, startWidth + deltaX);
      setColumnWidths(prev => ({
        ...prev,
        [colKey]: newWidth
      }));
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleToggleArchiveLead = (id: string) => {
    const updated = leads.map((l) => {
      if (l.id === id) {
        const isArchived = !l.isArchived;
        const timestamp = new Date().toISOString();
        const newLog: ActivityLogEntry = {
          id: `log-${Date.now()}`,
          timestamp,
          action: isArchived ? 'Lead Archived' : 'Lead Restored',
          details: isArchived ? 'Lead archived' : 'Lead restored to directory',
          user: currentUser?.email || 'msterling@armellini.com',
        };
        const updatedLead = {
          ...l,
          isArchived,
          activityLog: [newLog, ...(l.activityLog || [])],
        };
        persistLead(updatedLead);
        return updatedLead;
      }
      return l;
    });
    saveLeads(updated);
  };

  // Status colors override state with localStorage persistence
  const [statusColors, setStatusColors] = useState<Record<string, string>>(() => {
    const saved = localStorage.getItem('leadtracker_status_colors');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const migrated = migrateStatusColors(parsed);
        localStorage.setItem('leadtracker_status_colors', JSON.stringify(migrated));
        return migrated;
      } catch (e) {}
    }
    return { ...DEFAULT_STATUS_COLORS };
  });

  const handleSaveStatusColors = (newColors: Record<string, string>) => {
    setStatusColors(newColors);
    localStorage.setItem('leadtracker_status_colors', JSON.stringify(newColors));
    saveUserPreferences({ statusColors: newColors });
  };

  // Bulk Email Compose Modal State
  const [bulkEmailModalOpen, setBulkEmailModalOpen] = useState(false);
  const [bulkEmailRecipients, setBulkEmailRecipients] = useState<string[]>([]);
  const [bulkEmailSubject, setBulkEmailSubject] = useState('');
  const [bulkEmailBody, setBulkEmailBody] = useState('');
  const [bulkEmailTemplate, setBulkEmailTemplate] = useState('outreach');
  const [bulkEmailSending, setBulkEmailSending] = useState(false);
  const [bulkEmailProgress, setBulkEmailProgress] = useState(0);
  const [bulkEmailSuccess, setBulkEmailSuccess] = useState(false);

  const handleBulkEmail = (emails: string[]) => {
    setBulkEmailRecipients(emails);
    setBulkEmailModalOpen(true);
    setBulkEmailSuccess(false);
    setBulkEmailProgress(0);
    setBulkEmailSending(false);
    
    // Default template outreach
    setBulkEmailSubject('Streamlining your cold-chain logistics: Partnership proposal');
    setBulkEmailBody(`Hello,\n\nI hope you're having a productive week.\n\nI'm ${currentUser?.displayName || 'Mark Sterling'}, Logistics Administrator at Armellini. I'm reaching out because we'd love to partner with your firm to handle your temperature-controlled truckload and LTL shipping needs.\n\nWith our dedicated refrigerated networks and specialized floral delivery infrastructure, we ensure your high-value cargo arrives on schedule, in perfect condition.\n\nAre you available for a brief call next Tuesday to explore custom pricing specs?\n\nBest regards,\n\n${currentUser?.displayName || 'Mark Sterling'}\nArmellini Logistics\n${currentUser?.email || 'msterling@armellini.com'}`);
    setBulkEmailTemplate('outreach');
  };

  const handleBulkEmailTemplateChange = (templateId: string) => {
    setBulkEmailTemplate(templateId);
    if (templateId === 'outreach') {
      setBulkEmailSubject('Streamlining your cold-chain logistics: Partnership proposal');
      setBulkEmailBody(`Hello,\n\nI hope you're having a productive week.\n\nI'm ${currentUser?.displayName || 'Mark Sterling'}, Logistics Administrator at Armellini. I'm reaching out because we'd love to partner with your firm to handle your temperature-controlled truckload and LTL shipping needs.\n\nWith our dedicated refrigerated networks and specialized floral delivery infrastructure, we ensure your high-value cargo arrives on schedule, in perfect condition.\n\nAre you available for a brief call next Tuesday to explore custom pricing specs?\n\nBest regards,\n\n${currentUser?.displayName || 'Mark Sterling'}\nArmellini Logistics\n${currentUser?.email || 'msterling@armellini.com'}`);
    } else if (templateId === 'quote') {
      setBulkEmailSubject('Action Required: Request a custom pricing quote with Armellini Logistics');
      setBulkEmailBody(`Hello,\n\nFollowing up on our initial connection, we'd love to prepare a formal, customized rate quotation for your regular lanes.\n\nPlease reply with your primary origin-destination lanes, monthly volume estimates, and equipment specifications (e.g. refrigerated reefers, standard vans) so our dispatch optimization team can calculate the most competitive logistics price points.\n\nWe look forward to serving you.\n\nWarm regards,\n\n${currentUser?.displayName || 'Mark Sterling'}\nArmellini Logistics\n${currentUser?.email || 'msterling@armellini.com'}`);
    } else if (templateId === 'credit') {
      setBulkEmailSubject('Setup your direct billing: Armellini Credit Application');
      setBulkEmailBody(`Hello,\n\nTo fast-track your direct billing terms and configure your company under our active dispatch scheduler, please complete our secure credit application link:\nhttps://www.armellini.com/credit-application\n\nOnce submitted, our logistics credit department will approve your account within 24 hours so we can start moving your freight immediately.\n\nBest regards,\n\n${currentUser?.displayName || 'Mark Sterling'}\nArmellini Logistics\n${currentUser?.email || 'msterling@armellini.com'}`);
    }
  };

  const handleSendBulkEmails = () => {
    setBulkEmailSending(true);
    setBulkEmailProgress(10);
    
    const interval = setInterval(() => {
      setBulkEmailProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setBulkEmailSending(false);
          setBulkEmailSuccess(true);
          
          // Inject an activity entry to all selected leads
          const updatedLeads = leads.map((l) => {
            if (l.email && bulkEmailRecipients.includes(l.email)) {
              const newLog: ActivityLogEntry = {
                id: `log-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
                timestamp: new Date().toISOString(),
                action: 'Bulk Email Sent',
                details: `Sent campaign: "${bulkEmailSubject}" via logistics outreach tool`,
                user: 'msterling@armellini.com',
              };
              return {
                ...l,
                activityLog: [newLog, ...(l.activityLog || [])],
              };
            }
            return l;
          });
          saveLeads(updatedLeads);
          
          // Automatically close modal after showing success screen
          setTimeout(() => {
            setBulkEmailModalOpen(false);
          }, 1800);
          
          return 100;
        }
        return prev + 15;
      });
    }, 150);
  };

  // Kick the user to their first allowed view if their role can't see the current one
  useEffect(() => {
    const allowed = ROLE_TABS[userRole];
    if (!allowed.includes(activeTab)) {
      setActiveTab(allowed[0]);
    }
  }, [userRole, activeTab]);

  // Toggle theme class on document element
  useEffect(() => {
    document.documentElement.classList.remove('dark', 'theme-armellini-blue', 'theme-armellini-dark');
    
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else if (theme === 'armellini-blue') {
      document.documentElement.classList.add('dark', 'theme-armellini-blue');
    } else if (theme === 'armellini-dark') {
      document.documentElement.classList.add('dark', 'theme-armellini-dark');
    }
    
    localStorage.setItem('leadtracker_theme_v3', theme);
  }, [theme]);

  // Save layout preference
  useEffect(() => {
    localStorage.setItem('leadtracker_layout', layoutMode);
  }, [layoutMode]);

  // Auto-open lead in SlideOver drawer if leadId URL parameter is provided (e.g. from Operations email link)
  useEffect(() => {
    if (!leads || leads.length === 0) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const targetLeadId = params.get('leadId') || params.get('lead');
      if (targetLeadId) {
        const targetLead = leads.find((l) => l.id === targetLeadId);
        if (targetLead) {
          setEditingLead(targetLead);
          setIsSlideOverOpen(true);
        }
      }
    } catch (err) {
      console.warn("Failed to parse leadId from URL params:", err);
    }
  }, [leads]);

  // Kanban Drag and Drop Pipeline advancement
  const handleUpdatePipeline = (id: string, pipeline: PipelineType) => {
    const updated = leads.map((l) => {
      if (l.id === id) {
        if (l.pipeline === pipeline) return l;
        const timestamp = new Date().toISOString();
        // Dropping into a section also gives the lead that section's status
        const newStatus = PIPELINE_TO_STATUS[pipeline] || l.status;
        const newLog: ActivityLogEntry = {
          id: `log-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
          timestamp,
          action: 'Pipeline Advanced',
          details: `Moved from "${l.pipeline}" to "${pipeline}" via Drag-and-Drop — status set to "${newStatus}"`,
          user: currentUser?.email || 'msterling@armellini.com',
        };
        const updatedLead = {
          ...l,
          pipeline,
          status: newStatus,
          lastContacted: 'Moved via Drag-and-Drop',
          activityLog: [newLog, ...(l.activityLog || [])],
        };
        persistLead(updatedLead);
        return updatedLead;
      }
      return l;
    });
    saveLeads(updated);
  };

  // Notify once per day about follow-ups that are due
  useEffect(() => {
    if (!currentUser || leads.length === 0) return;
    const today = new Date().toLocaleDateString('en-CA');
    const key = `crm_notified_followups_${today}`;
    if (localStorage.getItem(key)) return;
    const due = leads.filter(l =>
      l.followUpDate &&
      !l.followUpCompletedAt &&
      l.followUpDate.slice(0, 10) <= today &&
      !['Won', 'Complete', 'Lost'].includes(l.status)
    );
    if (due.length > 0) {
      try { localStorage.setItem(key, '1'); } catch { /* best-effort */ }
      if (due.length === 1) {
        addNotification(
          `Follow-up due: ${due[0].company || due[0].name}`,
          `${due[0].name} (${due[0].company || 'Lead'}) — Due today (${due[0].followUpDate}). Click to open record.`,
          due[0].id
        );
      } else {
        // Individual direct notifications for the first 5 leads so clicking ANY immediately opens that record
        due.slice(0, 5).forEach((l) => {
          addNotification(
            `Follow-up due: ${l.company || l.name}`,
            `${l.name} (${l.company || 'Lead'}) — Scheduled for ${l.followUpDate}. Click to open record.`,
            l.id
          );
        });
        if (due.length > 5) {
          addNotification(
            `${due.length} follow-ups due today`,
            `${due.length} total leads have follow-ups scheduled for today or earlier.`,
            due[0].id,
            due.map(l => l.id)
          );
        }
      }
    }
  }, [leads, currentUser]);

  // Attach a tracked Gmail reply to the lead's activity log
  const handleLeadEmailReply = (leadId: string, reply: { from: string; date: string; snippet: string; trackingId: string }) => {
    const leadName = leads.find(l => l.id === leadId)?.name || 'lead';
    addNotification(
      `Email reply: ${leadName}`,
      `${reply.from} replied on [#${reply.trackingId}]: ${reply.snippet}`,
      leadId
    );
    setLeads(prevLeads => {
      const updated = prevLeads.map((l) => {
        if (l.id !== leadId) return l;
        const newLog: ActivityLogEntry = {
          id: `log-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
          timestamp: new Date().toISOString(),
          action: 'Email Reply Received',
          details: `Reply from ${reply.from} on tracked email [#${reply.trackingId}]: "${reply.snippet}"`,
          user: reply.from,
        };
        const updatedLead = {
          ...l,
          lastContacted: reply.date || new Date().toISOString(),
          activityLog: [newLog, ...(l.activityLog || [])],
        };
        persistLead(updatedLead);
        return updatedLead;
      });
      localStorage.setItem('leadtracker_leads', JSON.stringify(updated));
      return updated;
    });
  };

  // Google OAuth Connection Handlers
  const handleConnectGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      // Retrieve Google OAuth access token
      const credential = GoogleAuthProvider.credentialFromResult(result);
      if (credential) {
        const token = (credential as any).accessToken;
        setAccessToken(token);
        localStorage.setItem('google_access_token', token);
        // Also sign into the forms workspace (forms.armellini.com backend) with
        // the same Google credential so Client Forms works without a second popup.
        bridgeFormsAuth(credential).catch(() => { /* Client Forms shows its own connect prompt */ });
      }
      setCurrentUser(result.user);
    } catch (error) {
      console.error("Google Auth connection failed:", error);
      alert("Failed to connect Google Workspace. Please verify your credentials.");
    }
  };

  const handleDisconnectGoogle = async () => {
    setAccessToken(null);
    localStorage.removeItem('google_access_token');
    await signOut(auth);
  };

  // Provisioning & User Management Handlers (Admin Only)
  // Adds the user to the CRM and emails them an invite from the signed-in
  // admin's own Gmail. Returns a note describing whether the invite was sent.
  const handleAddUser = async (email: string, name: string, role: UserRole): Promise<string> => {
    const uid = `user-${Date.now()}`;
    const newUser: CRMUser = {
      uid,
      email,
      displayName: name,
      role,
      createdAt: new Date().toISOString()
    };
    await saveCRMUser(newUser);
    setCrmUsersList((prev) => [...prev, newUser]);

    const ROLE_DESC: Record<UserRole, string> = {
      admin: 'Administrator — full access, including User Access Control',
      editor: 'CRM Editor — full access to CRM & System Maintenance (except User Access Control)',
      sales: 'Sales Agent — standard CRM access',
      warehouse: 'Warehouse Agent — Consignee & Shipper Directory, Analytics, and Non-Floral LTL',
    };


    if (!accessToken) {
      return 'User added, but no invite email was sent — connect Google Workspace first so invites can go out from your Gmail.';
    }

    try {
      const inviteLink = window.location.origin;
      const adminName = currentUser?.displayName || 'Your administrator';
      const htmlBody = `
        <div style="font-family: sans-serif; max-width: 600px; margin: 0 auto; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; background-color: #ffffff; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);">
          <!-- Header Image Banner -->
          <div style="background-color: #ffffff; border-bottom: 1px solid #f1f5f9; text-align: center;">
            <img src="${inviteLink}/invite_header.png" alt="Armellini Information Services" style="width: 100%; max-width: 600px; display: block; border: 0; margin: 0 auto;" />
          </div>

          <!-- Content Body -->
          <div style="padding: 30px 24px; color: #334155; font-size: 14px; line-height: 1.6;">
            <p style="margin-top: 0; font-weight: 600; font-size: 15px;">Dear ${name},</p>
            <p>You have been <span style="background-color: #fef08a; padding: 2px 4px; border-radius: 3px; font-weight: 600; color: #854d0e;">invited</span> to join the <strong>Armellini CRM workspace</strong>.</p>
            
            <!-- Details Card -->
            <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; margin: 20px 0; font-size: 13px;">
              <tr>
                <td style="padding: 16px;">
                  <table width="100%" cellpadding="0" cellspacing="0">
                    <tr>
                      <td style="padding-bottom: 8px; color: #64748b; width: 120px; font-weight: 600;">Invited By:</td>
                      <td style="padding-bottom: 8px; color: #1e293b; font-weight: 700;">${adminName}</td>
                    </tr>
                    <tr>
                      <td style="padding-bottom: 8px; color: #64748b; font-weight: 600;">Sign-in Email:</td>
                      <td style="padding-bottom: 8px; color: #1e293b; font-family: monospace; font-weight: 700;">${email}</td>
                    </tr>
                    <tr>
                      <td style="color: #64748b; font-weight: 600;">Access Level:</td>
                      <td style="color: #1e293b; font-weight: 700;">${ROLE_DESC[role]}</td>
                    </tr>
                  </table>
                </td>
              </tr>
            </table>

            <!-- Onboarding Instructions Card -->
            <div style="background-color: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; padding: 18px 20px; margin: 20px 0; font-size: 13px;">
              <p style="margin-top: 0; margin-bottom: 8px; font-weight: 800; color: #166534; font-size: 14px;">
                📋 Mandatory Onboarding: Set Up Your CRM Email Signature
              </p>
              <p style="margin-top: 0; color: #15803d; line-height: 1.5; margin-bottom: 8px;">
                When you sign in for the first time, please complete your onboarding by copying your official email signature from Outlook/Gmail and saving it in the CRM:
              </p>
              <ol style="margin: 0; padding-left: 20px; color: #166534; line-height: 1.6;">
                <li>Click the sign-in button below and log in with your <strong>@armellini.com</strong> Google account.</li>
                <li>Go to <strong>Manage My Profile</strong> (top right user menu).</li>
                <li>Copy your official email signature from Outlook, Gmail, or Apple Mail.</li>
                <li>Paste it into the <strong>CRM Email Signature</strong> editor and click <strong>Save Profile &amp; Signature</strong>.</li>
              </ol>
              <p style="margin-bottom: 0; margin-top: 10px; font-size: 11px; color: #15803d; font-weight: 700;">
                ✨ Your CRM Email Signature will automatically populate on all outgoing emails and template variable <code style="background-color: #dcfce7; padding: 2px 5px; border-radius: 4px; font-family: monospace;">{{User Signature}}</code>!
              </p>
            </div>

            <!-- Call to Action Button -->
            <div style="text-align: center; margin: 25px 0 15px 0;">
              <a href="${inviteLink}" style="display: inline-block; padding: 12px 28px; background-color: #1a2b4c; color: #ffffff; text-decoration: none; border-radius: 6px; font-weight: 700; font-size: 14px; box-shadow: 0 4px 6px -1px rgba(26, 43, 76, 0.2); transition: background-color 0.2s;">
                &rarr; Sign in to Armellini CRM &amp; Complete Onboarding
              </a>
            </div>

            <p style="text-align: center; font-size: 11px; color: #64748b; margin-top: 0;">
              Log in with your <span style="font-weight: 600; color: #0f172a;">@armellini.com</span> Google account to access.
            </p>

            <div style="margin-top: 30px; border-top: 1px solid #f1f5f9; pt-20px; font-size: 12px; color: #64748b;">
              <p style="margin-bottom: 0;">Please reply to this email if you have any questions.</p>
              <p style="margin-top: 4px; font-weight: 600; color: #334155;">
                Armellini Logistics
              </p>
            </div>
          </div>

          <!-- Footer Image Banner -->
          <div style="background-color: #ffffff; border-top: 1px solid #e2e8f0; text-align: center;">
            <img src="${inviteLink}/invite_footer.png" alt="Armellini Logistics - Adding The Logic To Logistics" style="width: 100%; max-width: 600px; display: block; border: 0; margin: 0 auto;" />
          </div>
        </div>
      `;

      await sendGmailEmail(
        accessToken,
        email,
        `${name}, You have been invited to Armellini Logistics CRM`,
        htmlBody,
        undefined,
        true
      );

      addNotification('Invite sent', `${name} (${email}) was invited as ${role} from your Gmail.`);
      return `User added and invite emailed to ${email} from your Google account.`;
    } catch (e: any) {
      return `User added, but the invite email failed to send: ${e.message || 'Gmail error'}. You can notify them manually.`;
    }
  };

  const handleUpdateUserRole = async (uid: string, role: UserRole) => {
    await updateCRMUserRole(uid, role);
    setCrmUsersList((prev) =>
      prev.map((u) => (u.uid === uid ? { ...u, role } : u))
    );
    if (currentUser && currentUser.uid === uid) {
      setRealUserRole(role);
    }
    if (impersonatedUser && impersonatedUser.uid === uid) {
      const updated = { ...impersonatedUser, role };
      setImpersonatedUser(updated);
      sessionStorage.setItem('armellini_impersonated_user', JSON.stringify(updated));
    }
  };

  const handleStartImpersonation = (targetUser: CRMUser) => {
    if (realUserRole !== 'admin') return;
    setImpersonatedUser(targetUser);
    sessionStorage.setItem('armellini_impersonated_user', JSON.stringify(targetUser));
    setIsImpersonateMenuOpen(false);
    setIsProfileOpen(false);

    // If the active tab is not accessible by the impersonated user's role, switch gracefully
    const allowed = ROLE_TABS[targetUser.role] || ['dashboard'];
    if (!allowed.includes(activeTab)) {
      setActiveTab(allowed[0] || 'dashboard');
    }

    addNotification(
      'Impersonation Active',
      `Now viewing CRM as ${targetUser.displayName} (${targetUser.role.toUpperCase()}). Role restrictions and data access apply.`
    );
  };

  const handleStopImpersonation = () => {
    setImpersonatedUser(null);
    sessionStorage.removeItem('armellini_impersonated_user');
    setIsImpersonateMenuOpen(false);
    addNotification(
      'Impersonation Ended',
      'Returned to your Administrator workspace.'
    );
  };

  const handleDeleteUser = async (uid: string) => {
    setCrmUsersList((prev) => prev.filter((u) => u.uid !== uid));
    if (impersonatedUser?.uid === uid) {
      handleStopImpersonation();
    }
    const cached = localStorage.getItem("crm_users_cache");
    if (cached) {
      const users = JSON.parse(cached) as CRMUser[];
      const filtered = users.filter((u) => u.uid !== uid);
      localStorage.setItem("crm_users_cache", JSON.stringify(filtered));
    }
  };

  const handleDevBypass = () => {
    const devUser = {
      uid: 'admin-msterling',
      email: 'msterling@armellini.com',
      displayName: 'Local Dev Admin',
    };
    setCurrentUser(devUser);
    setDbUser({
      uid: 'admin-msterling',
      email: 'msterling@armellini.com',
      displayName: 'Local Dev Admin',
      role: 'admin',
      createdAt: new Date().toISOString(),
    });
    setRealUserRole('admin');
    setAuthReady(true);
  };

  // 1. Firebase Auth state listener & local dev auto-login
  useEffect(() => {
    // If running on localhost / dev mode, auto-bypass login immediately
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      handleDevBypass();
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setCurrentUser(firebaseUser);

        // Silently re-link the forms workspace (Client Forms) on session
        // restore so the module works without any extra sign-in step.
        const storedToken = localStorage.getItem('google_access_token');
        if (storedToken) {
          bridgeFormsAuthWithToken(storedToken).catch(() => { /* FormsHub retries on open */ });
        }
      } else if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        handleDevBypass();
      } else {
        // Signed out — the login page gates the app until Google sign-in
        setCurrentUser(null);
        setAccessToken(null);
        setAccessDenied(null);
        setDbUser(null);
        localStorage.removeItem('google_access_token');
        sessionStorage.removeItem('armellini_impersonated_user');
        setImpersonatedUser(null);
        setRealUserRole('sales');
      }
      setAuthReady(true);
    });

    return () => unsubscribe();
  }, []);

  // 2. Resolve the CRM profile and role from the server. /api/profile is
  // readable by every provisioned user, unlike /api/users which is admin-only,
  // so this is the authoritative source for the caller's own role.
  useEffect(() => {
    if (!currentUser) return;
    if (currentUser.uid === 'admin-msterling') return;
    let cancelled = false;

    const loadProfile = async () => {
      try {
        const headers = await getAuthHeaders();
        const res = await fetch('/api/profile', { headers });
        if (cancelled) return;

        if (res.status === 403) {
          const body = await res.json().catch(() => ({} as any));
          setAccessDenied(
            body.error ||
            'Your account has not been granted access to the Armellini CRM. Please contact an administrator.'
          );
          setDbUser(null);
          return;
        }
        if (!res.ok) {
          throw new Error(`Profile request failed with ${res.status}`);
        }

        const profile = await res.json();
        if (cancelled) return;
        setAccessDenied(null);
        setDbUser(profile);
        const resolvedRole = normalizeRole(profile?.role);
        setRealUserRole(resolvedRole);
        if (resolvedRole !== 'admin') {
          setImpersonatedUser(null);
          sessionStorage.removeItem('armellini_impersonated_user');
        }
      } catch (err) {
        console.warn("Failed to load user profile:", err);
      }
    };

    loadProfile();
    return () => { cancelled = true; };
  }, [currentUser?.uid]);

  // 3. The user roster powers Lead Owner assignment, team directories, and User Access Control.
  useEffect(() => {
    if (!currentUser) {
      setCrmUsersList([]);
      return;
    }
    fetchCRMUsers()
      .then(setCrmUsersList)
      .catch((err) => console.warn('Failed to load the CRM user roster:', err));
  }, [currentUser?.uid]);

  // 4. Restore saved dashboard personalisation (columns, filters, sort, views).
  useEffect(() => {
    if (!currentUser || accessDenied) return;
    let cancelled = false;
    fetchUserPreferences()
      .then((prefs) => {
        if (cancelled || !prefs) return;
        if (Array.isArray(prefs.columns) && prefs.columns.length > 0) setColumnPreferences(normalizeColumnPreferences(prefs.columns));
        if (Array.isArray(prefs.filters)) setAdvancedRules(prefs.filters);
        if (Array.isArray(prefs.sort)) setMultiSortRules(prefs.sort);
        if (Array.isArray(prefs.smartViews)) setSmartViews(prefs.smartViews);
        if (prefs.statusColors && typeof prefs.statusColors === 'object' && Object.keys(prefs.statusColors).length > 0) {
          const migrated = migrateStatusColors(prefs.statusColors);
          setStatusColors(migrated);
          localStorage.setItem('leadtracker_status_colors', JSON.stringify(migrated));
        }
      })
      .catch((err) => console.warn('Failed to load dashboard preferences:', err));
    return () => { cancelled = true; };
  }, [currentUser?.uid, accessDenied]);

  // Helper to normalize legacy status and pipeline values to strict options
  const normalizeLead = (lead: Lead): Lead => {
    const legacyStatusMap: Record<string, string> = {
      'New': 'Lead',
      'New Lead': 'Lead',
      'Website Lead': 'Lead',
      'Attempted to Contact': 'Lead',
      'Cold Call Lead': 'Lead',
      'Sent Quote': 'Quoted',
      'Sent Credit App': 'Quoted',
      'Awarded': 'Approved',
      'Accepted': 'Approved',
      'Customer Setup': 'Approved',
      'In Process': 'Approved',
      'Working on it': 'Approved',
      'Awaiting First Shipment': 'Approved',
      'Won': 'Approved',
      'Onboarding': 'Finalizing',
      'Broker': 'Transferred',
      'Brokers': 'Transferred',
      'Shipper': 'Transferred',
      'Lost': 'Declined',
      'Revisit Later | ADD FU DATE': 'Declined',
    };

    const legacyPipelineMap: Record<string, PipelineType> = {
      'Website Leads': 'Opportunity',
      'New Leads': 'Opportunity',
      'Attempted to Contact': 'Opportunity',
      'Cold Call Leads': 'Opportunity',
      'Quoted': 'Opportunity',
      'Onboarding': 'Won',
      'In Process': 'Won',
      'Awaiting First Shipment': 'Won',
      'Brokers': 'Unqualified',
      'Shipper': 'Unqualified',
      'Revisit Later | ADD FU DATE': 'Lose',
      'Complete': 'Won',
    };

    const isCustomOrCanonicalStatus =
      STATUS_DISPLAY_LABELS.includes(lead.status as any) ||
      Boolean(statusColors && lead.status && statusColors[lead.status]);
    const status = legacyStatusMap[lead.status] || (isCustomOrCanonicalStatus ? lead.status : 'Lead');
    const pipeline = legacyPipelineMap[lead.pipeline] || (PIPELINE_BOARD_CATEGORIES.includes(lead.pipeline as any) ? (lead.pipeline as PipelineType) : 'Opportunity');

    // Preserve only real scheduled followUpDate values; never fabricate followUpDate from historical inquiryDate
    const followUpDate = lead.followUpDate ? lead.followUpDate.trim() || undefined : undefined;

    return {
      ...lead,
      status,
      pipeline,
      followUpDate,
    };
  };

  // 2. Fetch leads from Firestore (seamlessly falls back to localStorage)
  const loadLeads = useCallback(async () => {
    setIsLeadsLoading(true);
    try {
      const fbLeads = await fetchLeadsFromFirestore();
      let loadedLeads = fbLeads.length > 0 ? fbLeads : INITIAL_LEADS;
      
      const leadsWithLogs = loadedLeads.map((l) => {
        const normalized = normalizeLead(l);
        if (!normalized.activityLog || normalized.activityLog.length === 0) {
          return { ...normalized, activityLog: seedActivityLog(normalized) };
        }
        return normalized;
      });

      // Initialize known lead IDs if not set yet
      try {
        const knownRaw = localStorage.getItem('crm_known_lead_ids');
        if (!knownRaw && leadsWithLogs.length > 0) {
          localStorage.setItem('crm_known_lead_ids', JSON.stringify(leadsWithLogs.map(l => l.id)));
        }
      } catch {}

      setLeads(leadsWithLogs);
      localStorage.setItem('leadtracker_leads', JSON.stringify(leadsWithLogs));
    } finally {
      setIsLeadsLoading(false);
    }
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser) return;
    loadLeads();
  }, [loadLeads, currentUser]);

  // 2b. Automated Leads Refresh Timer (silent background updates every 30s when tab is visible)
  useEffect(() => {
    if (!autoRefreshEnabled || !currentUser?.uid) return;

    const silentLoad = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const fbLeads = await fetchLeadsFromFirestore();
        if (fbLeads.length > 0) {
          let knownIds: string[] = [];
          try {
            knownIds = JSON.parse(localStorage.getItem('crm_known_lead_ids') || '[]');
          } catch {}

          if (knownIds.length > 0) {
            const newLeads = fbLeads.filter(l => !knownIds.includes(l.id));
            for (const nl of newLeads) {
              const isOnlineForm = nl.activityLog?.some(a => a.action === 'Lead Captured') || nl.owner === 'sales@armellini.com' || nl.status === 'Lead';
              if (isOnlineForm) {
                addNotification(
                  `New Lead Captured: ${nl.company || nl.name}`,
                  `Contact: ${nl.name || 'N/A'} • Service: ${nl.service || 'Freight'} • Click to view in CRM`,
                  nl.id
                );
              }
            }
          }

          try {
            localStorage.setItem('crm_known_lead_ids', JSON.stringify(fbLeads.map(l => l.id)));
          } catch {}

          const leadsWithLogs = fbLeads.map((l) => {
            const normalized = normalizeLead(l);
            return {
              ...normalized,
              activityLog: normalized.activityLog && normalized.activityLog.length > 0 ? normalized.activityLog : seedActivityLog(normalized)
            };
          });
          setLeads(leadsWithLogs);
          localStorage.setItem('leadtracker_leads', JSON.stringify(leadsWithLogs));
        }
      } catch (err) {
        console.warn("Silent background leads refresh failed:", err);
      }
    };

    const interval = setInterval(silentLoad, 30_000);
    return () => clearInterval(interval);
  }, [autoRefreshEnabled, currentUser?.uid]);

  // Save leads to local storage on change
  const saveLeads = (updatedLeads: Lead[]) => {
    setLeads(updatedLeads);
    localStorage.setItem('leadtracker_leads', JSON.stringify(updatedLeads));
  };

  // Realtime network/activity pulse status sync simulator
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsOnline(false);
    }, 10000);
    return () => clearTimeout(timer);
  }, []);

  // Handle slide-over saves (both create & edit)
  const handleSaveLead = (leadData: Omit<Lead, 'id'> & { id?: string }) => {
    if (leadData.id) {
      // Edit
      const oldLead = leads.find((l) => l.id === leadData.id);
      let newLogs: ActivityLogEntry[] = [];
      if (oldLead) {
        newLogs = getLeadChanges(oldLead, leadData, currentUser?.email || 'sales-rep');
        // Follow-up newly marked complete → activity entry + a stamped line in Quick Notes
        if (leadData.followUpCompletedAt && leadData.followUpCompletedAt !== oldLead.followUpCompletedAt) {
          const who = currentUser?.displayName || currentUser?.email || 'Unknown user';
          const when = new Date(leadData.followUpCompletedAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
          newLogs.unshift({
            id: `log-${Date.now()}-fu`,
            timestamp: leadData.followUpCompletedAt,
            action: 'Follow-up Completed',
            details: `Follow-up${leadData.followUpDate ? ` scheduled for ${leadData.followUpDate}` : ''} marked done`,
            user: currentUser?.email || 'sales-rep',
          });
          // Prepend the completion to the append-only Quick Notes log
          const noteLine = `📅 ${when} — ${who}\n✅ Follow-up completed${leadData.followUpDate ? ` (was scheduled for ${leadData.followUpDate})` : ''}`;
          leadData.quickNotes = leadData.quickNotes?.trim()
            ? `${noteLine}\n\n${leadData.quickNotes.trim()}`
            : noteLine;
        }
      }
      
      const newDate = leadData.followUpDate;
      const oldDate = oldLead ? oldLead.followUpDate : undefined;
      
      if (newDate && newDate !== oldDate && accessToken) {
        // Create Calendar Event for Follow-up
        createFollowUpEvent(
          accessToken,
          leadData.name,
          leadData.company,
          newDate,
          leadData.owner // Pass the owner's email so they get the invite
        ).catch(err => {
          console.error("Failed to create Google Calendar follow-up event:", err);
        });
      }

      const updated = leads.map((l) => {
        if (l.id === leadData.id) {
          const combinedLogs = [...newLogs, ...(l.activityLog || [])];
          const updatedLead = {
            ...l,
            ...leadData,
            lastContacted: 'Modified just now',
            activityLog: combinedLogs,
          };
          persistLead(updatedLead);
          return updatedLead;
        }
        return l;
      });
      saveLeads(updated);
    } else {
      // Create
      const todayDateEntered = new Date().toISOString().slice(0, 10);
      const dateEntered = leadData.inquiryDate || todayDateEntered;
      const followUpDate = leadData.followUpDate || dateEntered;
      const newLeadItem: Lead = {
        ...leadData,
        id: `lead-${Date.now()}`,
        inquiryDate: dateEntered,
        followUpDate,
        lastContacted: 'Added just now',
        activityLog: [
          {
            id: `log-${Date.now()}`,
            timestamp: new Date().toISOString(),
            action: 'Lead Created',
            details: 'Initial entry created',
            user: currentUser?.email || 'msterling@armellini.com',
          }
        ]
      } as Lead;
      
      if (leadData.followUpDate && accessToken) {
        // Create Calendar Event for Follow-up
        createFollowUpEvent(
          accessToken,
          leadData.name,
          leadData.company,
          leadData.followUpDate,
          leadData.owner
        ).catch(err => {
          console.error("Failed to create Google Calendar follow-up event:", err);
        });
      }

      persistLead(newLeadItem);
      saveLeads([newLeadItem, ...leads]);
    }
    setEditingLead(null);
    setDefaultPipeline(null);
  };

  // Delete lead
  const handleDeleteLead = (id: string) => {
    const filtered = leads.filter((l) => l.id !== id);
    saveLeads(filtered);
    deleteLeadFromFirestore(id);
  };

  // Quick inline update for status changes
  const handleUpdateStatus = (id: string, status: string) => {
    const updated = leads.map((l) => {
      if (l.id === id) {
        const timestamp = new Date().toISOString();
        // A status change moves the lead to its matching board section
        const targetPipeline = STATUS_TO_PIPELINE[status] || l.pipeline;
        const moved = targetPipeline !== l.pipeline;
        const newLog: ActivityLogEntry = {
          id: `log-${Date.now()}`,
          timestamp,
          action: 'Status Updated',
          details: `Status changed from "${l.status}" to "${status}"${moved ? ` — moved to "${targetPipeline}" board` : ''}`,
          user: currentUser?.email || 'msterling@armellini.com',
        };
        const updatedLead = {
          ...l,
          status,
          pipeline: targetPipeline,
          lastContacted: 'Status updated',
          activityLog: [newLog, ...(l.activityLog || [])],
        };
        persistLead(updatedLead);
        return updatedLead;
      }
      return l;
    });
    saveLeads(updated);
  };

  // Bulk status update for multiple leads
  const handleBulkUpdateStatus = (ids: string[], status: string) => {
    const updated = leads.map((l) => {
      if (ids.includes(l.id)) {
        const timestamp = new Date().toISOString();
        const newLog: ActivityLogEntry = {
          id: `log-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
          timestamp,
          action: 'Bulk Status Updated',
          details: `Bulk status changed from "${l.status}" to "${status}"`,
          user: currentUser?.email || 'msterling@armellini.com',
        };
        const updatedLead = {
          ...l,
          status,
          pipeline: STATUS_TO_PIPELINE[status] || l.pipeline,
          lastContacted: 'Status updated via Bulk Action',
          activityLog: [newLog, ...(l.activityLog || [])],
        };
        persistLead(updatedLead);
        return updatedLead;
      }
      return l;
    });
    saveLeads(updated);
  };

  // Bulk delete leads
  const handleBulkDeleteLeads = (ids: string[]) => {
    const filtered = leads.filter((l) => !ids.includes(l.id));
    saveLeads(filtered);
    for (const id of ids) {
      deleteLeadFromFirestore(id);
    }
  };

  // Trigger editing modal
  const handleEditLead = (lead: Lead) => {
    setEditingLead(lead);
    setDefaultPipeline(lead.pipeline);
    setIsSlideOverOpen(true);
  };

  const handleQuoteLead = (acctOrLead: any) => {
    if (acctOrLead.id && (acctOrLead.name || acctOrLead.company)) {
      // It's an existing lead
      setEditingLead(acctOrLead);
      setDefaultPipeline(acctOrLead.pipeline);
    } else {
      // It's a new lead from account
      const companyName = acctOrLead.company || acctOrLead.accountName || acctOrLead.account_name || acctOrLead.name || acctOrLead.company_name || acctOrLead.Customer_Name || acctOrLead.shippername || '';
      const acctNo = acctOrLead.accountNumber || acctOrLead.account_number || acctOrLead.Account_Number || acctOrLead.shipper_account || '';
      const contactName = acctOrLead.full_name || acctOrLead.contactPerson || acctOrLead.name || (companyName ? `${companyName} Contact` : '');

      setEditingLead({
        id: crypto.randomUUID(),
        name: contactName,
        company: companyName,
        accountNumber: acctNo,
        email: acctOrLead.email || '',
        phone: acctOrLead.phone || acctOrLead.Phone || acctOrLead.emergency_phone || '',
        location: acctOrLead.location || (acctOrLead.city && acctOrLead.state ? `${acctOrLead.city}, ${acctOrLead.state}` : acctOrLead.City && acctOrLead.state ? `${acctOrLead.City}, ${acctOrLead.state}` : ''),
        dealValue: 0,
        pipeline: 'Opportunity',
        status: 'Quoted',
        owner: currentUser?.displayName || currentUser?.email || '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      setDefaultPipeline('Opportunity');
    }
    setIsSlideOverOpen(true);
  };

  // Trigger create modal pre-filled with pipeline category
  const handleAddNewLeadToBoard = (pipeline: PipelineType) => {
    setEditingLead(null);
    setDefaultPipeline(pipeline);
    setIsSlideOverOpen(true);
  };

  // CSV Export utility
  const handleExportCSV = () => {
    const headers = [
      'Lead ID', 'Name', 'Company', 'Care Of', 'Bill-To Account #', 'Email', 'Phone', 'Owner', 
      'Contact Person', 'Service', 'Industry', 'Location', 
      'Deal Value ($)', 'Pipeline Group', 'Status', 'Lead Notes', 'Quote Details',
      'Credit App Sent', 'Rate Agreement Sent', 'Internal Emails Sent', 'First Delivery Follow Up'
    ];
    const rows = leads.map((l) => [
      l.id,
      `"${(l.name || '').replace(/"/g, '""')}"`,
      `"${(l.company || '').replace(/"/g, '""')}"`,
      `"${(l.careOf || '').replace(/"/g, '""')}"`,
      `"${(l.billToAccountNumber || '').replace(/"/g, '""')}"`,
      l.email || '',
      l.phone || '',
      l.owner || '',
      `"${(l.contactPerson || '').replace(/"/g, '""')}"`,
      l.service || '',
      l.industry || '',
      l.location || '',
      l.dealValue,
      l.pipeline,
      l.status,
      `"${(l.leadNotes || l.nextSteps || '').replace(/"/g, '""')}"`,
      `"${(l.quoteDetails || '').replace(/"/g, '""')}"`,
      l.creditAppSentAt || '',
      l.rateAgreementSentAt || '',
      l.internalEmailsSentAt || '',
      l.firstDeliveryFollowUpAt || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((e) => e.join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'Armellini_CRM_Leads.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Restores initial default contacts
  const handleResetDefaults = () => {
    saveLeads(INITIAL_LEADS);
    setIsProfileOpen(false);
  };

  // Calculate stats for metrics calculations
  const totalVal = leads.reduce((acc, l) => acc + l.dealValue, 0);
  const wonLeads = leads.filter((l) => l.status === 'Won' || l.status === 'Approved' || l.status === 'Awarded' || l.status === 'Finalizing' || l.status === 'Onboarding' || l.status === 'Complete');
  const workingLeads = leads.filter((l) => l.status === 'Finalizing' || l.status === 'Onboarding' || l.status === 'Working on it' || l.status === 'In Process');
  const lostLeads = leads.filter((l) => l.status === 'Lost' || l.status === 'Not Awarded');

  // Distinct dropdown options for the Filter menu (from the current leads)
  const distinct = (key: keyof Lead) =>
    Array.from(new Set(leads.map((l) => (l[key] as string) || '').filter(Boolean))).sort();
  const ownerOptions = useMemo(() => {
    const set = new Set<string>(distinct('owner') as string[]);
    AUTHORIZED_SALESPERSONS.forEach((p) => set.add(p));
    (crmUsersList || []).forEach((u) => {
      if (u.role === 'sales' || u.role === 'editor' || u.role === 'admin') {
        const name = u.displayName || u.email.split('@')[0];
        if (name) set.add(name);
      }
    });
    return Array.from(set).filter(Boolean).sort();
  }, [leads, crmUsersList]);
  const serviceOptions = distinct('service');
  const industryOptions = distinct('industry');

  function evaluateFilterRule(lead: Lead, rule: FilterRule): boolean {
    if (!rule.field) return true;
    let val: any = (lead as any)[rule.field];
    if (rule.field === 'lastContacted' || rule.field === 'lastUpdateDate') {
      const d = getLeadLastUpdateDate(lead);
      if (d) val = d.toISOString().slice(0, 10);
    }

    if (Array.isArray(val)) {
      val = val.join(', ');
    }

    const strVal = (val !== undefined && val !== null) ? String(val).toLowerCase() : '';
    const ruleVal = (rule.value || '').toLowerCase();

    switch (rule.operator) {
      case 'contains':
        return strVal.includes(ruleVal);
      case 'does_not_contain':
        return !strVal.includes(ruleVal);
      case 'equals':
        return strVal === ruleVal;
      case 'not_equals':
        return strVal !== ruleVal;
      case 'greater_than':
        return Number(val || 0) > Number(rule.value || 0);
      case 'less_than':
        return Number(val || 0) < Number(rule.value || 0);
      case 'is_empty':
        return !val || strVal.trim() === '';
      case 'is_not_empty':
        return Boolean(val) && strVal.trim() !== '';
      case 'relative_date': {
        if (!val) return false;
        const targetDate = new Date(val);
        if (isNaN(targetDate.getTime())) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        switch (ruleVal) {
          case 'today':
            return targetDate.toDateString() === today.toDateString();
          case 'yesterday': {
            const yest = new Date(today);
            yest.setDate(yest.getDate() - 1);
            return targetDate.toDateString() === yest.toDateString();
          }
          case 'last 7 days': {
            const past = new Date(today);
            past.setDate(past.getDate() - 7);
            return targetDate >= past && targetDate <= today;
          }
          case 'last 30 days': {
            const past = new Date(today);
            past.setDate(past.getDate() - 30);
            return targetDate >= past && targetDate <= today;
          }
          case 'next 30 days': {
            const future = new Date(today);
            future.setDate(future.getDate() + 30);
            return targetDate >= today && targetDate <= future;
          }
          default:
            return true;
        }
      }
      default:
        return strVal.includes(ruleVal);
    }
  }

  function applyMultiSort(leadsList: Lead[], sortRules: SortRule[]): Lead[] {
    if (!sortRules || sortRules.length === 0) return leadsList;

    return [...leadsList].sort((a, b) => {
      for (const rule of sortRules) {
        let aVal = (a as any)[rule.field];
        let bVal = (b as any)[rule.field];

        if (Array.isArray(aVal)) aVal = aVal.join(', ');
        if (Array.isArray(bVal)) bVal = bVal.join(', ');

        if (typeof aVal === 'number' || typeof bVal === 'number') {
          aVal = Number(aVal || 0);
          bVal = Number(bVal || 0);
        } else {
          aVal = (aVal || '').toString().toLowerCase();
          bVal = (bVal || '').toString().toLowerCase();
        }

        if (aVal < bVal) return rule.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return rule.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }

  // Apply Filter + Sort to the leads before they are grouped/rendered
  const displayedLeads = (() => {
    let arr = leads;
    if (!includeArchived) {
      arr = arr.filter((l) => !l.isArchived);
    }
    arr = arr.filter((l) =>
      (!filters.owner || l.owner === filters.owner) &&
      (!filters.status || l.status === filters.status) &&
      (!filters.service || l.service === filters.service) &&
      (!filters.industry || l.industry === filters.industry) &&
      (!filters.state || l.outboundState === filters.state || l.state === filters.state)
    );
    if (filters.lastUpdate) {
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      arr = arr.filter((l) => {
        const lastUpdateDate = getLeadLastUpdateDate(l);
        if (!lastUpdateDate) return false;

        if (filters.lastUpdate === 'today') {
          return lastUpdateDate >= todayStart;
        }
        if (filters.lastUpdate === '7days') {
          const sevenDaysAgo = new Date(todayStart.getTime() - 6 * 24 * 60 * 60 * 1000);
          return lastUpdateDate >= sevenDaysAgo;
        }
        if (filters.lastUpdate === '30days') {
          const thirtyDaysAgo = new Date(todayStart.getTime() - 29 * 24 * 60 * 60 * 1000);
          return lastUpdateDate >= thirtyDaysAgo;
        }
        if (filters.lastUpdate === 'custom') {
          if (filters.lastUpdateCustomStart) {
            const start = new Date(filters.lastUpdateCustomStart + 'T00:00:00');
            if (lastUpdateDate < start) return false;
          }
          if (filters.lastUpdateCustomEnd) {
            const end = new Date(filters.lastUpdateCustomEnd + 'T23:59:59');
            if (lastUpdateDate > end) return false;
          }
          return true;
        }
        return true;
      });
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      arr = arr.filter(l => 
        (l.name || '').toLowerCase().includes(q) ||
        (l.company || '').toLowerCase().includes(q) ||
        (l.consigneeName && l.consigneeName.toLowerCase().includes(q)) ||
        (l.shipperName && l.shipperName.toLowerCase().includes(q)) ||
        (l.careOf && l.careOf.toLowerCase().includes(q)) ||
        (l.billToAccountNumber && l.billToAccountNumber.toLowerCase().includes(q)) ||
        (l.leadNotes && l.leadNotes.toLowerCase().includes(q)) ||
        (l.owner && l.owner.toLowerCase().includes(q)) ||
        (l.service && l.service.toLowerCase().includes(q)) ||
        (l.industry && l.industry.toLowerCase().includes(q)) ||
        (l.location && l.location.toLowerCase().includes(q)) ||
        (l.status && l.status.toLowerCase().includes(q))
      );
    }
    if (advancedRules.length > 0) {
      arr = arr.filter(lead => {
        return advancedRules.every(rule => evaluateFilterRule(lead, rule));
      });
    }
    if (multiSortRules.length > 0) {
      arr = applyMultiSort(arr, multiSortRules);
    } else if (sortField !== 'default') {
      arr = sortLeadsByField(arr, sortField, sortDir, getLeadLastUpdateDate);
    } else {
      arr = sortLeadsDefault(arr);
    }
    return arr;
  })();

  // Group displayedLeads if groupBy is set (for flat Table View)
  const tableGroups = useMemo(() => {
    if (layoutMode !== 'table') return [];
    if (groupBy === 'lastUpdateDate') {
      const values = UPDATE_GROUP_ORDER.filter(v => displayedLeads.some(l => getLeadUpdateGroup(l) === v));
      return values.map(val => ({
        groupValue: val,
        leads: displayedLeads.filter(l => getLeadUpdateGroup(l) === val)
      }));
    }
    const values = Array.from(
      new Set(displayedLeads.map(l => String((l as any)[groupBy] || 'Unspecified')))
    ).sort();
    return values.map(val => ({
      groupValue: val,
      leads: displayedLeads.filter(l => String((l as any)[groupBy] || 'Unspecified') === val)
    }));
  }, [displayedLeads, groupBy, layoutMode]);


  const activeStageConfig = STAGES_CONFIG.find(s => s.id === activeStageTab);
  const activeStagePipelines = activeStageConfig ? activeStageConfig.pipelines : [];

  // Build the sections to render based on Group By
  const GROUP_PALETTE = ['#0086f0', '#00c875', '#fdab3d', '#a25ddc', '#e2445c', '#6366f1', '#f43f5e', '#22c55e', '#579bfc', '#64748b'];
  const dashboardGroups: { key: string; title: string; color: string; leads: Lead[] }[] =
    groupBy === 'pipeline'
      ? BOARDS_CONFIG.filter((b) => activeStagePipelines.includes(b.type)).map((b) => ({
          key: b.type,
          title: b.type,
          color: b.color,
          leads: displayedLeads.filter((l) => l.pipeline === b.type),
        }))
      : groupBy === 'lastUpdateDate'
      ? UPDATE_GROUP_ORDER.filter((v) => displayedLeads.some((l) => getLeadUpdateGroup(l) === v)).map((v, i) => ({
          key: v,
          title: v,
          color: GROUP_PALETTE[i % GROUP_PALETTE.length],
          leads: displayedLeads.filter((l) => getLeadUpdateGroup(l) === v),
        }))
      : (() => {
          const values: string[] = Array.from(new Set<string>(displayedLeads.map((l): string => String((l as any)[groupBy] ?? 'None')))).sort();
          return values.map((v, i) => ({
            key: v,
            title: v,
            color: groupBy === 'status' ? (resolveStatusColor(v, statusColors) || GROUP_PALETTE[i % GROUP_PALETTE.length]) : GROUP_PALETTE[i % GROUP_PALETTE.length],
            leads: displayedLeads.filter((l) => String((l as any)[groupBy] ?? 'None') === v),
          }));
        })();


  const normalizedPath = window.location.pathname.toLowerCase().replace(/\/+$/, '') || '/';

  // PUBLIC ROUTE: Customer online forms (/apply/:inviteId)
  if (normalizedPath.startsWith('/apply/')) {
    const inviteId = window.location.pathname.split('/apply/')[1]?.split('/')[0]?.split('?')[0] || '';
    return <PublicFormRunner inviteId={inviteId} />;
  }

  // PUBLIC ROUTE: the lead capture form needs no sign-in (mirrors the Monday form)
  if (normalizedPath === '/lead-form' || normalizedPath.startsWith('/lead-form/')) {
    return <LeadCaptureForm />;
  }

  // AUTH GATE: splash while Firebase restores the session, login page when signed out
  if (!authReady) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-slate-100 dark:bg-slate-900">
        <div className="flex flex-col items-center gap-3">
          <div className="bg-white rounded-xl px-4 py-2.5 shadow-lg animate-pulse">
            <img src="/logo.png" alt="Armellini Logistics" className="h-10 object-contain" />
          </div>
          <p className="text-xs font-bold text-slate-400 font-mono tracking-widest uppercase">Loading Workspace…</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-slate-100 dark:bg-slate-900 p-4 transition-colors">
        <div className="w-full max-w-md bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl p-8 text-center">
          <div className="bg-white rounded-xl px-5 py-3 inline-block mx-auto">
            <img src="/logo.png" alt="Armellini Logistics" className="h-14 object-contain" />
          </div>
          <h1 className="mt-5 text-xl font-extrabold tracking-tight text-slate-900 dark:text-white">CRM Workspace</h1>
          <p className="mt-1.5 text-sm text-slate-500 dark:text-slate-400">
            Pipeline management, logistics, and Google Workspace — sign in with your Armellini Google account to continue.
          </p>
          <button
            onClick={handleConnectGoogle}
            className="mt-7 w-full flex items-center justify-center gap-3 bg-blue-600 hover:bg-blue-700 text-white font-bold px-5 py-3 rounded-xl text-sm transition-all shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 cursor-pointer"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/>
              <path fill="#e2e8f0" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
              <path fill="#cbd5e1" d="M5.84 14.1A6.6 6.6 0 0 1 5.49 12c0-.73.13-1.44.35-2.1V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/>
              <path fill="#f1f5f9" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
            </svg>
            Sign in with Google
          </button>

          {typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname === '0.0.0.0') && (
            <button
              onClick={handleDevBypass}
              className="mt-3 w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-5 py-3 rounded-xl text-sm transition-all shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 cursor-pointer"
            >
              <ShieldCheck className="w-4 h-4" />
              Bypass Login (Local Dev Mode)
            </button>
          )}
          <p className="mt-5 text-[11px] text-slate-400 dark:text-slate-500">
            Access is managed by your workspace administrator. Signing in requests the Gmail, Drive, Sheets, Calendar, Chat, and Contacts permissions the CRM uses.
          </p>
          <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-800 flex flex-col items-center justify-center gap-1.5 font-mono">
            <div className="flex items-center justify-center gap-2">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">Version</span>
              <span className="font-black text-lg text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/60 px-2.5 py-0.5 rounded-md border border-blue-200 dark:border-blue-800/80">
                {APP_VERSION}
              </span>
            </div>
            <div className="text-xs font-bold text-slate-500 dark:text-slate-400">
              Build: {APP_BUILD_DATETIME}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ACCESS GATE: signed in, but the server has not granted this account access.
  // Accounts are provisioned by an administrator; nothing is created implicitly.
  if (accessDenied) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-slate-100 dark:bg-slate-900 p-4 transition-colors">
        <div className="w-full max-w-md bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl p-8 text-center">
          <div className="bg-white rounded-xl px-5 py-3 inline-block mx-auto">
            <img src="/logo.png" alt="Armellini Logistics" className="h-14 object-contain" />
          </div>
          <div className="mt-6 mx-auto w-11 h-11 rounded-full bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 flex items-center justify-center">
            <ShieldAlert className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          </div>
          <h1 className="mt-4 text-lg font-extrabold tracking-tight text-slate-900 dark:text-white">Access not granted</h1>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">{accessDenied}</p>
          {currentUser?.email && (
            <p className="mt-3 text-[11px] font-mono text-slate-400 dark:text-slate-500 break-all">
              Signed in as {currentUser.email}
            </p>
          )}
          <button
            onClick={handleDisconnectGoogle}
            className="mt-6 w-full flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-900 dark:bg-slate-800 dark:hover:bg-slate-700 text-white font-bold px-5 py-2.5 rounded-xl text-sm transition-all cursor-pointer"
          >
            <LogOut className="w-4 h-4" /> Sign out and try another account
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-900 text-black dark:text-slate-100 h-screen w-screen overflow-hidden flex flex-col font-sans transition-colors duration-150">
      {isUat && (
        <div className="bg-red-600 text-white font-extrabold text-xs py-2 px-4 text-center tracking-widest uppercase shrink-0 shadow-md flex items-center justify-center gap-2 z-[100]">
          <span>USER ACCEPTANCE TESTING (UAT)</span>
        </div>
      )}

      {/* USER IMPERSONATION ACTIVE BANNER */}
      {isImpersonating && impersonatedUser && (
        <div className="bg-gradient-to-r from-amber-600 via-orange-600 to-amber-700 text-white px-4 md:px-6 py-2.5 shadow-md flex items-center justify-between z-[90] text-xs shrink-0 border-b border-amber-500/50">
          <div className="flex items-center gap-3 min-w-0">
            <span className="p-1.5 bg-black/20 rounded-lg text-sm shrink-0 flex items-center justify-center">
              🎭
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono font-bold uppercase tracking-wider text-[10px] bg-black/30 px-2 py-0.5 rounded text-amber-200">
                  Impersonation Active
                </span>
                <strong className="text-white text-xs truncate">{impersonatedUser.displayName || impersonatedUser.email}</strong>
                <span className="text-amber-100/80 font-mono text-[11px] truncate hidden sm:inline">({impersonatedUser.email})</span>
                <span className="bg-amber-900/80 text-amber-100 uppercase font-bold tracking-wider text-[10px] px-2 py-0.5 rounded border border-amber-400/40 font-mono">
                  Role: {impersonatedUser.role}
                </span>
              </div>
              <p className="text-[11px] text-amber-100/90 hidden md:block mt-0.5 truncate">
                Previewing CRM with this user&apos;s permissions, data views, and role guardrails. All financial &amp; workspace tabs reflect their level.
              </p>
            </div>
          </div>
          <button
            onClick={handleStopImpersonation}
            className="bg-black/40 hover:bg-black/60 active:bg-black/80 text-white font-bold text-xs px-3.5 py-1.5 rounded-lg border border-white/30 transition flex items-center gap-1.5 cursor-pointer shrink-0 shadow-sm ml-3"
          >
            <UserX className="w-3.5 h-3.5" /> Stop Impersonating
          </button>
        </div>
      )}

      {/* TOP STICKY ANNOUNCEMENT BANNER */}
      {topBanners.length > 0 && (
        <BannerBar
          announcements={topBanners}
          onDismiss={handleDismissBanner}
          onOpenModal={(ann) => {
            setSelectedSpotlightAnnouncement(ann);
            setIsWhatNewModalOpen(true);
          }}
          onNavigateTab={(tab) => setActiveTab(tab)}
        />
      )}

      <div className="flex-1 flex min-h-0 w-full overflow-hidden">
        {/* 1. LEFT SIDEBAR (Desktop) */}
        <div className={`transition-all duration-300 ease-in-out flex overflow-hidden shrink-0 ${sidebarOpen ? 'w-64 opacity-100' : 'w-0 opacity-0 pointer-events-none'}`}>
        <Sidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          userRole={userRole}
          nonFloralTab={nonFloralTab}
          onNonFloralNav={(tab) => { setNonFloralTab(tab); setActiveTab('non-floral'); }}
        />
      </div>

      {/* 2. MAIN WORKSPACE */}
      <main className="flex-1 flex flex-col min-w-0 bg-white dark:bg-slate-900 relative z-10 overflow-hidden">
        
        {/* TOP GLOBAL HEADER */}
        <header className="h-14 bg-white dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 px-4 md:px-6 flex items-center justify-between shrink-0 shadow-sm z-30 transition-colors">
          <div className="flex items-center gap-2.5">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-1.5 rounded-lg text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-900 transition-colors cursor-pointer hidden md:flex items-center justify-center border border-transparent hover:border-slate-200 dark:hover:border-slate-800"
              title={sidebarOpen ? "Hide Navigation Menu" : "Show Navigation Menu"}
            >
              {sidebarOpen ? <ChevronLeft className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
            <h1 className="text-base md:text-lg font-black text-slate-800 dark:text-slate-100 tracking-tight flex items-center gap-2">
              Pipeline Management
            </h1>
            <span className="hidden sm:inline-block bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-400 text-[10px] font-bold px-2 py-0.5 rounded font-mono">
              Live Active CRM
            </span>

            {/* Auto-Refresh Sync Pill placed next to Pipeline Management */}
            <div
              title="Leads automatically sync in the background every 30 seconds. Click to pause/resume or force instant refresh."
              onClick={() => setAutoRefreshEnabled(!autoRefreshEnabled)}
              className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-100/80 dark:bg-slate-900/80 border border-slate-200/80 dark:border-slate-800 rounded-lg text-[10px] font-black text-slate-600 dark:text-slate-400 select-none cursor-pointer hover:bg-slate-200/80 dark:hover:bg-slate-800 transition active:scale-[0.98] shadow-inner ml-1"
            >
              <span className={`w-1.5 h-1.5 rounded-full ${autoRefreshEnabled ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`} />
              <span>{autoRefreshEnabled ? `Auto-sync (${autoRefreshCountdown}s)` : 'Auto-refresh paused'}</span>
              <RefreshCw
                className="w-3 h-3 text-slate-400 hover:text-blue-600 transition-transform cursor-pointer ml-0.5"
                onClick={(e) => {
                  e.stopPropagation();
                  loadLeads();
                  setAutoRefreshCountdown(30);
                }}
                title="Force Instant Refresh"
              />
            </div>
          </div>
          
          <div className="flex items-center gap-2 md:gap-4">
            {/* Notification Center */}
            <NotificationBell
              notifications={notifications}
              leads={leads}
              onMarkAllRead={() => updateNotifications(notifications.map(n => ({ ...n, read: true })))}
              onMarkRead={(id) => updateNotifications(notifications.map(n => n.id === id ? { ...n, read: true } : n))}
              onClearAll={() => updateNotifications([])}
              onSelectLead={handleSelectLeadById}
            />

            {/* AI CRM Advisor */}
            <button
              onClick={() => setIsAIAdvisorOpen(true)}
              className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-purple-650 dark:text-purple-400 hover:text-purple-805 dark:hover:text-purple-300 transition cursor-pointer flex items-center justify-center border border-purple-100 dark:border-purple-950/60 bg-purple-50/50 dark:bg-purple-950/20"
              title="Open AI CRM Smart Advisor"
            >
              <Sparkles className="w-4 h-4" />
            </button>

            {/* What's New Feature Spotlight Trigger */}
            <button
              onClick={() => {
                setSelectedSpotlightAnnouncement(showcaseAnnouncements[0] || announcements[0] || null);
                setIsWhatNewModalOpen(true);
              }}
              className="relative p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition cursor-pointer flex items-center justify-center border border-indigo-100 dark:border-indigo-950/60 bg-indigo-50/50 dark:bg-indigo-950/20"
              title="What's New & Feature Updates"
            >
              <Megaphone className="w-4 h-4" />
              {showcaseAnnouncements.length > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-600"></span>
                </span>
              )}
            </button>

            {/* Help Center */}
            <button
              onClick={() => setActiveTab('help-center')}
              className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition cursor-pointer flex items-center justify-center border border-transparent hover:border-slate-200 dark:hover:border-slate-800"
              title="Help Center & Video Tutorials"
            >
              <HelpCircle className="w-4 h-4" />
            </button>

            {/* Global Theme Selector Popover */}
            <div className="relative">
              <button
                onClick={() => setIsThemeMenuOpen(!isThemeMenuOpen)}
                className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100 transition cursor-pointer flex items-center justify-center border border-transparent hover:border-slate-200 dark:hover:border-slate-800"
                title="Select Color Mode"
              >
                <Palette className="w-4 h-4" />
              </button>

              <AnimatePresence>
                {isThemeMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setIsThemeMenuOpen(false)} />
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: 8 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: 8 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 mt-2 w-56 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl z-50 p-2 space-y-1"
                    >
                      <div className="px-2.5 py-1.5 text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                        Color Theme
                      </div>
                      
                      <button
                        onClick={() => { setTheme('light'); setIsThemeMenuOpen(false); }}
                        className={`w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-xs font-bold transition cursor-pointer ${
                          theme === 'light'
                            ? 'bg-slate-100 dark:bg-slate-900 text-slate-900 dark:text-slate-100'
                            : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900/50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm">☀️</span>
                          <span>Light Mode (Default)</span>
                        </div>
                        {theme === 'light' && <Check className="w-3.5 h-3.5 text-blue-500" />}
                      </button>

                      <button
                        onClick={() => { setTheme('dark'); setIsThemeMenuOpen(false); }}
                        className={`w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-xs font-bold transition cursor-pointer ${
                          theme === 'dark'
                            ? 'bg-slate-100 dark:bg-slate-900 text-slate-900 dark:text-slate-100'
                            : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900/50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm">🌙</span>
                          <span>Original Dark Mode</span>
                        </div>
                        {theme === 'dark' && <Check className="w-3.5 h-3.5 text-blue-500" />}
                      </button>

                      <button
                        onClick={() => { setTheme('armellini-blue'); setIsThemeMenuOpen(false); }}
                        className={`w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-xs font-bold transition cursor-pointer ${
                          theme === 'armellini-blue'
                            ? 'bg-slate-100 dark:bg-slate-900 text-slate-900 dark:text-slate-100'
                            : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900/50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm">🔵</span>
                          <span>Armellini Blue Theme</span>
                        </div>
                        {theme === 'armellini-blue' && <Check className="w-3.5 h-3.5 text-blue-500" />}
                      </button>

                      <button
                        onClick={() => { setTheme('armellini-dark'); setIsThemeMenuOpen(false); }}
                        className={`w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-xs font-bold transition cursor-pointer ${
                          theme === 'armellini-dark'
                            ? 'bg-slate-100 dark:bg-slate-900 text-slate-900 dark:text-slate-100'
                            : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900/50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm">🌑</span>
                          <span>Armellini Dark Theme</span>
                        </div>
                        {theme === 'armellini-dark' && <Check className="w-3.5 h-3.5 text-blue-500" />}
                      </button>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>

            <div className="w-px h-5 bg-slate-200 dark:bg-slate-800 mx-1"></div>

            {/* ADMIN USER IMPERSONATION SWITCHER */}
            {realUserRole === 'admin' && (
              <div className="relative">
                <button
                  onClick={() => setIsImpersonateMenuOpen(!isImpersonateMenuOpen)}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-bold transition shadow-xs cursor-pointer border ${
                    isImpersonating
                      ? 'bg-amber-600 hover:bg-amber-700 text-white border-amber-500 ring-2 ring-amber-400/30'
                      : 'bg-amber-50 hover:bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:hover:bg-amber-900/60 dark:text-amber-300 border-amber-200 dark:border-amber-800/60'
                  }`}
                  title="Admin Impersonation: Preview the CRM as any user"
                >
                  <UserCheck className="w-3.5 h-3.5 shrink-0" />
                  <span className="hidden xl:inline">
                    {isImpersonating ? `🎭 Impersonating: ${impersonatedUser?.displayName?.split(' ')[0] || 'User'}` : 'Impersonate'}
                  </span>
                  <ChevronDown className="w-3 h-3 opacity-70" />
                </button>

                {isImpersonateMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setIsImpersonateMenuOpen(false)}
                    />
                    <div className="absolute right-0 mt-2 w-80 rounded-xl bg-white dark:bg-slate-950 shadow-2xl ring-1 ring-black/5 dark:ring-slate-800 focus:outline-none z-50 border border-slate-200 dark:border-slate-800 p-3">
                      <div className="flex items-center justify-between pb-2 mb-2 border-b border-slate-100 dark:border-slate-800">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm">🎭</span>
                          <span className="text-xs font-extrabold uppercase tracking-wider text-slate-800 dark:text-slate-100">
                            Impersonate User
                          </span>
                        </div>
                        {isImpersonating && (
                          <button
                            onClick={handleStopImpersonation}
                            className="text-[10px] font-bold text-red-600 dark:text-red-400 hover:underline cursor-pointer"
                          >
                            Exit
                          </button>
                        )}
                      </div>

                      <div className="relative mb-2">
                        <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2" />
                        <input
                          type="text"
                          value={impersonateFilter}
                          onChange={(e) => setImpersonateFilter(e.target.value)}
                          placeholder="Search team members..."
                          className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg pl-8 pr-2.5 py-1.5 text-xs text-slate-800 dark:text-slate-100 outline-none focus:border-amber-500"
                        />
                      </div>

                      <div className="max-h-56 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800 rounded-lg border border-slate-100 dark:border-slate-800">
                        {crmUsersList
                          .filter(u => u.email.toLowerCase() !== currentUser?.email?.toLowerCase())
                          .filter(u => !impersonateFilter || u.displayName.toLowerCase().includes(impersonateFilter.toLowerCase()) || u.email.toLowerCase().includes(impersonateFilter.toLowerCase()) || u.role.toLowerCase().includes(impersonateFilter.toLowerCase()))
                          .map(u => {
                            const isCurrent = impersonatedUser?.uid === u.uid;
                            return (
                              <button
                                key={u.uid}
                                onClick={() => handleStartImpersonation(u)}
                                className={`w-full p-2 text-left flex items-center justify-between hover:bg-amber-50/60 dark:hover:bg-amber-950/30 transition text-xs cursor-pointer ${
                                  isCurrent ? 'bg-amber-100/70 dark:bg-amber-950/50 font-bold' : ''
                                }`}
                              >
                                <div className="min-w-0 pr-2">
                                  <div className="text-slate-800 dark:text-slate-200 truncate font-semibold">
                                    {u.displayName}
                                  </div>
                                  <div className="text-[10px] text-slate-400 dark:text-slate-500 truncate">
                                    {u.email}
                                  </div>
                                </div>
                                <span className="shrink-0 text-[9px] font-mono font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                                  {u.role}
                                </span>
                              </button>
                            );
                          })}
                        {crmUsersList.filter(u => u.email.toLowerCase() !== currentUser?.email?.toLowerCase()).length === 0 && (
                          <div className="p-4 text-center text-xs text-slate-400">
                            No team members found
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="w-px h-5 bg-slate-200 dark:bg-slate-800 mx-1"></div>

            {/* Prominent Export Button */}
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-800 dark:bg-slate-800 text-white dark:text-slate-200 rounded-md text-xs font-bold hover:bg-slate-900 dark:hover:bg-slate-700 transition shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-500 cursor-pointer"
              title="Export leads database to CSV"
            >
              <DownloadCloud className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Export CSV</span>
            </button>
            
            <div className="w-px h-5 bg-slate-200 dark:bg-slate-800 mx-1"></div>

            {/* ACCESSIBLE USER PROFILE PILL */}
            <div className="relative inline-block text-left">
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="group relative flex items-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-800 p-1 pr-3 rounded-full border border-transparent hover:border-slate-200 dark:hover:border-slate-700 transition-colors focus:outline-none cursor-pointer"
              >
                <div className="relative shrink-0">
                  <img
                    src={(currentUser as any)?.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(currentUser?.displayName || currentUser?.email || 'User')}&background=0D8ABC&color=fff&bold=true`}
                    alt={currentUser?.displayName || 'User avatar'}
                    className="w-7 h-7 rounded-full border border-white dark:border-slate-800"
                  />
                  <span
                    className={`absolute bottom-0 right-0 w-2.5 h-2.5 border-2 border-white dark:border-slate-950 rounded-full z-10 transition-colors duration-500 ${
                      isOnline ? 'bg-emerald-500' : 'bg-amber-500'
                    }`}
                  ></span>
                  {isOnline && (
                    <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse opacity-75 z-0"></span>
                  )}
                </div>
                <span className="hidden lg:inline text-xs font-bold text-slate-700 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-slate-100">
                  {dbUser?.displayName || currentUser?.displayName || currentUser?.email || 'Not signed in'}
                </span>
                <ChevronDown className="w-3 h-3 text-slate-400" />
              </button>

              {/* Dropdown Content */}
              {isProfileOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setIsProfileOpen(false)}
                  />
                  <div className="absolute right-0 mt-2 w-[340px] md:w-[400px] origin-top-right rounded-xl bg-white dark:bg-slate-950 shadow-xl ring-1 ring-black ring-opacity-5 dark:ring-slate-800 focus:outline-none z-50 border border-slate-100 dark:border-slate-800">
                    <div className="p-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 rounded-t-xl flex justify-between items-center">
                      <div>
                        <p className="text-sm font-bold text-slate-900 dark:text-slate-100">{dbUser?.displayName || currentUser?.displayName || currentUser?.email || 'Team Member'}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                          {realUserRole === 'admin' ? 'Logistics Administrator' : realUserRole === 'editor' ? 'CRM Editor' : realUserRole === 'warehouse' ? 'Warehouse Agent' : 'Sales Agent'}
                        </p>
                      </div>
                      <span className="text-[10px] bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-400 font-mono font-bold px-2 py-0.5 rounded">
                        {realUserRole === 'admin' ? 'Admin Account' : realUserRole === 'editor' ? 'CRM Editor' : realUserRole === 'warehouse' ? 'Warehouse Agent' : 'Sales Agent'}
                      </span>
                    </div>

                    {isImpersonating && impersonatedUser && (
                      <div className="p-3 bg-amber-500/10 border-b border-amber-500/20 flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                          <span className="text-sm">🎭</span>
                          <div>
                            <div className="font-bold text-amber-900 dark:text-amber-200">
                              Impersonating {impersonatedUser.displayName}
                            </div>
                            <div className="text-[10px] text-amber-700 dark:text-amber-400">
                              Active role: {impersonatedUser.role.toUpperCase()}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={handleStopImpersonation}
                          className="px-2.5 py-1 bg-amber-600 hover:bg-amber-700 text-white font-bold text-[11px] rounded-md transition cursor-pointer"
                        >
                          Exit
                        </button>
                      </div>
                    )}
                    
                    <div className="p-4">
                      {/* Navigate Directly to User Profile */}
                      <button
                        onClick={() => {
                          setIsProfileOpen(false);
                          setActiveTab('profile');
                        }}
                        className="w-full mb-3 flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-xs font-bold text-slate-700 dark:text-slate-350 hover:bg-slate-50 dark:hover:bg-slate-900 hover:text-blue-600 dark:hover:text-blue-400 transition cursor-pointer border border-slate-200 dark:border-slate-800 shadow-sm"
                      >
                        <User className="w-4 h-4 text-blue-600" />
                        Manage My Profile
                      </button>

                      <div className="border border-slate-200 dark:border-slate-800 rounded-xl p-4 flex flex-col bg-white dark:bg-slate-950">
                        <div className="text-slate-400 dark:text-slate-500 font-mono text-[9px] font-bold tracking-wider uppercase">
                          WORKSPACE SYSTEM
                        </div>
                        <h4 className="text-base font-bold mt-1 text-slate-800 dark:text-slate-200">
                          Community License <span className="text-xs text-slate-400 dark:text-slate-500 font-normal">/ Active</span>
                        </h4>
                        <p className="text-xs mt-2 text-slate-500 dark:text-slate-400 leading-relaxed">
                          Direct real-time cloud database synchronization, customizable categories, secure workspace connections, and interactive pipelines.
                        </p>
                      </div>
                    </div>

                    <div className="p-3 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 rounded-b-xl flex justify-between items-center text-xs">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => {
                            setIsProfileOpen(false);
                            handleDisconnectGoogle();
                          }}
                          className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 hover:text-red-700 dark:hover:text-red-300 px-3 py-1.5 rounded font-bold flex items-center gap-1 transition cursor-pointer"
                        >
                          <LogOut className="w-3.5 h-3.5" /> Sign Out
                        </button>
                        <button
                          onClick={handleResetDefaults}
                          className="text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 px-3 py-1.5 rounded font-bold flex items-center gap-1 transition cursor-pointer"
                        >
                          <RefreshCw className="w-3.5 h-3.5" /> Reset CRM Data
                        </button>
                      </div>
                      <span className="text-slate-400 dark:text-slate-500 font-mono text-[10px]">
                        Pulse: {isOnline ? 'Online' : 'Idle'}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* MANDATORY ONBOARDING BANNER FOR MISSING CRM EMAIL SIGNATURE */}
        {currentUser && (!dbUser?.signature || !dbUser?.signature.trim()) && (
          <div className="bg-amber-500/10 dark:bg-amber-500/15 border-b border-amber-500/30 px-4 md:px-6 py-3 text-xs text-amber-900 dark:text-amber-200 flex flex-wrap items-center justify-between gap-3 shadow-xs">
            <div className="flex items-center gap-2.5">
              <Sparkles className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
              <div>
                <strong className="font-extrabold text-amber-900 dark:text-amber-200">Action Required: Complete Your CRM Onboarding</strong>
                <p className="text-[11px] text-amber-800/90 dark:text-amber-300/90 mt-0.5">
                  Copy your official email signature from Outlook / Gmail and paste it into your CRM Profile settings so template variable <code className="bg-amber-200/60 dark:bg-amber-900/60 px-1 py-0.5 rounded font-mono font-bold text-amber-950 dark:text-amber-200">&#123;&#123;User Signature&#125;&#125;</code> populates automatically on all outgoing messages.
                </p>
              </div>
            </div>
            <button
              onClick={() => setActiveTab('profile')}
              className="px-3.5 py-1.5 bg-amber-600 hover:bg-amber-700 active:bg-amber-800 text-white font-bold text-xs rounded-lg shadow-xs transition shrink-0 cursor-pointer flex items-center gap-1.5"
            >
              <User className="w-3.5 h-3.5" /> Go to Profile &amp; Paste Signature &rarr;
            </button>
          </div>
        )}

        {/* MOBILE NAVIGATION TABS BAR (Visible only on mobile) */}
        <div className="flex md:hidden bg-white dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 shrink-0">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
            { id: 'contacts', label: 'Contacts', icon: Users },
            { id: 'non-floral', label: 'LTL Cargo', icon: Layers },
            { id: 'analytics', label: 'Analytics', icon: BarChart2 },
            { id: 'google-hub', label: 'Google', icon: Globe },
            { id: 'help-center', label: 'Help Docs', icon: BookOpen },
            ...(userRole === 'admin' ? [{ id: 'user-mgmt', label: 'Access', icon: ShieldAlert }] : [])
          ].filter((t) => ROLE_TABS[userRole].includes(t.id)).map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`flex-1 py-3 text-center text-xs font-bold flex flex-col items-center justify-center gap-1 border-b-2 transition ${
                isActive ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'border-transparent text-slate-500'
                }`}
              >
                <Icon className="w-4 h-4" />
                {item.label}
              </button>
            );
          })}
        </div>

        {/* CONSOLIDATED STICKY TOOLBAR (Search, View Controls, Quick Filters, Sort/Group, Refresh, Add Lead) */}
        {(activeTab === 'dashboard' || activeTab === 'contacts' || activeTab === 'analytics') && (
          <QuickFiltersBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            activeTab={activeTab}
            layoutMode={layoutMode}
            onChangeLayoutMode={setLayoutMode}
            ownerOptions={ownerOptions}
            selectedOwner={filters.owner}
            onSelectOwner={(val) => setFilters((f) => ({ ...f, owner: val }))}
            selectedService={filters.service}
            onSelectService={(val) => setFilters((f) => ({ ...f, service: val }))}
            selectedState={filters.state || ''}
            onSelectState={(val) => setFilters((f) => ({ ...f, state: val }))}
            selectedStatus={filters.status}
            onSelectStatus={(val) => setFilters((f) => ({ ...f, status: val }))}
            selectedLastUpdate={filters.lastUpdate}
            onSelectLastUpdate={(val) => setFilters((f) => ({ ...f, lastUpdate: val }))}
            customStartDate={filters.lastUpdateCustomStart}
            onSelectCustomStart={(val) => setFilters((f) => ({ ...f, lastUpdateCustomStart: val }))}
            customEndDate={filters.lastUpdateCustomEnd}
            onSelectCustomEnd={(val) => setFilters((f) => ({ ...f, lastUpdateCustomEnd: val }))}
            includeArchived={includeArchived}
            onToggleIncludeArchived={setIncludeArchived}
            sortField={sortField}
            onSelectSortField={setSortField}
            sortDir={sortDir}
            onSelectSortDir={setSortDir}
            groupBy={groupBy}
            onSelectGroupBy={setGroupBy}
            onOpenAdvancedFilters={() => setIsAdvancedFilterOpen(true)}
            onOpenColumnCustomizer={() => setIsColumnCustomizerOpen(true)}
            onResetFilters={() => {
              setFilters({ owner: '', status: '', service: '', industry: '', state: '', lastUpdate: '', lastUpdateCustomStart: '', lastUpdateCustomEnd: '' });
              setAdvancedRules([]);
              setMultiSortRules([]);
            }}
            activeFilterCount={activeFilterCount + advancedRules.length + (multiSortRules.length > 0 ? 1 : 0)}
            autoRefreshEnabled={autoRefreshEnabled}
            onToggleAutoRefresh={() => setAutoRefreshEnabled(!autoRefreshEnabled)}
            autoRefreshCountdown={autoRefreshCountdown}
            onAddNewLead={() => {
              setEditingLead(null);
              setDefaultPipeline('New Leads');
              setIsSlideOverOpen(true);
            }}
          />
        )}

        {/* MAIN BODY AREA */}
        <div className="flex-1 overflow-y-auto px-3 md:px-5 py-6 bg-slate-100 dark:bg-slate-900 transition-colors">
          <div className="w-full">
            
            {/* VIEW 1: DASHBOARD TAB */}
            {activeTab === 'dashboard' && (
              <>
                {/* Smart View Presets Toolbar */}
                <SmartViewSelector
                  smartViews={smartViews}
                  activeViewId={activeViewId}
                  onSelectView={(view) => {
                    if (!view) {
                      setActiveViewId(null);
                      return;
                    }
                    setActiveViewId(view.id);
                    if (view.columns) setColumnPreferences(view.columns);
                    if (view.filterRules) setAdvancedRules(view.filterRules);
                    if (view.sortRules) setMultiSortRules(view.sortRules);
                  }}
                  onSaveCurrentView={(name) => {
                    const newView: SmartView = {
                      id: `sv-${Date.now()}`,
                      name,
                      filterRules: advancedRules,
                      sortRules: multiSortRules,
                      columns: columnPreferences,
                      createdAt: new Date().toISOString(),
                    };
                    const updatedViews = [...smartViews, newView];
                    setSmartViews(updatedViews);
                    setActiveViewId(newView.id);
                    saveUserPreferences({ smartViews: updatedViews });
                  }}
                  onDeleteView={(id) => {
                    const updatedViews = smartViews.filter(v => v.id !== id);
                    setSmartViews(updatedViews);
                    if (activeViewId === id) setActiveViewId(null);
                    saveUserPreferences({ smartViews: updatedViews });
                  }}
                  hasActiveFilters={advancedRules.length > 0 || multiSortRules.length > 0}
                />



                {/* KPI Metrics */}
                <KPICards leads={leads} userRole={userRole} />

                {/* Upcoming Deadlines Widget */}
                <UpcomingDeadlines
                  leads={leads}
                  selectedOwner={filters.owner}
                  onEditLead={handleEditLead}
                  userRole={userRole}
                  isLoading={isLeadsLoading}
                />

                {/* Chevron Progress Flow Stage Tabs */}
                {layoutMode !== 'table' && (
                  <div className="chevron-tabs-container shrink-0 select-none">
                    {STAGES_CONFIG.map((stage) => {
                      const isActive = activeStageTab === stage.id;
                      
                      // Calculate leads count and total deal value for this stage
                      const stageLeads = displayedLeads.filter(l => stage.pipelines.includes(l.pipeline));
                      const stageCount = stageLeads.length;
                      const stageValue = stageLeads.reduce((sum, l) => sum + l.dealValue, 0);

                      return (
                        <button
                          key={stage.id}
                          type="button"
                          onClick={() => {
                            setActiveStageTab(stage.id);
                            if (layoutMode === 'table') {
                              setLayoutMode('pipeline');
                            }
                          }}
                          className={`chevron-tab ${isActive ? 'active' : ''}`}
                          style={{
                            '--active-color': stage.color,
                          } as React.CSSProperties}
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-extrabold text-[12px] tracking-wider uppercase">{stage.label.toUpperCase()}</span>
                            <span className={`text-[13px] font-black font-sans px-2 py-0.5 rounded-full ${isActive ? 'bg-white/25 text-white' : 'bg-slate-200 text-slate-800 dark:bg-slate-800 dark:text-slate-200'}`}>
                              {stageCount}
                            </span>
                          </div>
                          {canViewFinancials && (
                            <div className={`text-[10px] font-mono mt-0.5 ${isActive ? 'text-white/85' : 'text-slate-500 dark:text-slate-400'}`}>
                              ${stageValue.toLocaleString()}
                            </div>
                          )}

                        </button>
                      );
                    })}
                  </div>
                )}


                 {layoutMode === 'pipeline' && (
                  /* Section Grouped Boards (Pipeline View) */
                  <div className="space-y-10 pb-12">
                    {dashboardGroups.filter((board) => board.leads.length > 0 || groupBy === 'pipeline').map((board) => {
                      return (
                        <BoardTable
                          key={board.key}
                          title={board.title as PipelineType}
                          color={board.color}
                          leads={board.leads}
                          onUpdateStatus={handleUpdateStatus}
                          onDeleteLead={handleDeleteLead}
                          onEditLead={handleEditLead}
                          onAddNewLeadToBoard={handleAddNewLeadToBoard}
                          searchQuery={searchQuery}
                          onBulkUpdateStatus={handleBulkUpdateStatus}
                          onBulkDeleteLeads={handleBulkDeleteLeads}
                          statusColors={statusColors}
                          onBulkEmail={handleBulkEmail}
                          onMoveLead={groupBy === 'pipeline' ? handleUpdatePipeline : () => {}}
                          statusOptions={STATUS_DISPLAY_LABELS}
                          columnPreferences={columnPreferences}
                          activeSortField={sortField}
                          activeSortDirection={sortDir}
                          onHeaderSort={(field) => {
                            if (sortField === field) {
                              setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                            } else {
                              setSortField(field);
                              setSortDir('asc');
                            }
                          }}
                        />
                      );
                    })}
                  </div>
                )}

                 {layoutMode === 'table' && (
                  /* Section Flat Spreadsheet View (Table View) */
                  <div className="bg-white dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden transition-colors pb-6 mb-12">
                    
                    {/* Dedicated Table Controls: Search, Filter, Sort, Group By */}
                    <div className="p-3 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 flex flex-col sm:flex-row items-center justify-between gap-4 select-none">
                      <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
                        {/* Search Input */}
                        <div className="relative w-full sm:w-80">
                          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                          <input
                            type="text"
                            placeholder="Search leads (Name, Company, Owner, Service, Industry)..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-9 pr-4 py-1.5 bg-slate-100 dark:bg-slate-900 border border-transparent dark:border-slate-850 rounded-md text-xs text-slate-800 dark:text-slate-100 focus:bg-white dark:focus:bg-slate-950 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all placeholder:text-slate-500 dark:placeholder:text-slate-400 shadow-inner"
                          />
                        </div>

                        <div className="w-px h-5 bg-slate-200 dark:bg-slate-800 mx-1 hidden sm:block"></div>

                        {/* FILTER */}
                        <div className="relative">
                          <button
                            onClick={() => setOpenMenu(openMenu === 'tableFilter' ? null : 'tableFilter')}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-wider rounded-lg shadow-sm cursor-pointer transition-all ${
                              activeFilterCount
                                ? 'border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400'
                                : 'border border-slate-200 dark:border-slate-800 bg-slate-50 hover:bg-slate-100 dark:bg-slate-900 dark:hover:bg-slate-850 text-slate-800 dark:text-slate-100'
                            }`}
                          >
                            <Filter className="w-3.5 h-3.5" /> FILTER{activeFilterCount ? ` (${activeFilterCount})` : ''}
                          </button>
                          {openMenu === 'tableFilter' && (
                            <>
                              <div className="fixed inset-0 z-40" onClick={() => setOpenMenu(null)} />
                              <div className="absolute left-0 mt-2 w-64 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl z-50 p-3 space-y-3">
                                {([
                                  { key: 'owner' as const, label: 'Owner', opts: ownerOptions },
                                  { key: 'status' as const, label: 'Status', opts: Object.keys(statusColors) },
                                  { key: 'service' as const, label: 'Service', opts: serviceOptions },
                                  { key: 'industry' as const, label: 'Industry', opts: industryOptions },
                                ]).map(({ key, label, opts }) => (
                                  <div key={key}>
                                    <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">{label}</label>
                                    <select
                                      value={filters[key]}
                                      onChange={(e) => setFilters((f) => ({ ...f, [key]: e.target.value }))}
                                      className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded px-2 py-1.5 text-xs text-slate-800 dark:text-slate-100 outline-none cursor-pointer"
                                    >
                                      <option value="">All</option>
                                      {opts.map((o) => <option key={o} value={o}>{o}</option>)}
                                    </select>
                                  </div>
                                ))}
                                {activeFilterCount > 0 && (
                                  <button
                                    onClick={() => setFilters({ owner: '', status: '', service: '', industry: '' })}
                                    className="w-full text-[11px] font-bold text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 rounded py-1.5 transition cursor-pointer"
                                  >
                                    Clear all filters
                                  </button>
                                )}
                              </div>
                            </>
                          )}
                        </div>

                        {/* SORT */}
                        <div className="relative">
                          <button
                            onClick={() => setOpenMenu(openMenu === 'tableSort' ? null : 'tableSort')}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-wider rounded-lg shadow-sm cursor-pointer transition-all ${
                              sortField !== 'default'
                                ? 'border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400'
                                : 'border border-slate-200 dark:border-slate-800 bg-slate-50 hover:bg-slate-100 dark:bg-slate-900 dark:hover:bg-slate-850 text-slate-800 dark:text-slate-100'
                            }`}
                          >
                            <ArrowUpDown className="w-3.5 h-3.5" /> SORT
                          </button>
                          {openMenu === 'tableSort' && (
                            <>
                              <div className="fixed inset-0 z-40" onClick={() => setOpenMenu(null)} />
                              <div className="absolute left-0 mt-2 w-52 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl z-50 p-1.5">
                                {([
                                  { v: 'default', label: 'Default (Alerts & New Leads First)' },
                                  { v: 'status', label: 'Status (Lead First)' },
                                  { v: 'name', label: 'Name / Company' },
                                  { v: 'dealValue', label: 'Deal Value' },
                                  { v: 'followUpDate', label: 'Follow-up Date' },
                                  { v: 'lastContacted', label: 'Last Interaction' },
                                ] as const).map(({ v, label }) => (
                                  <button
                                    key={v}
                                    onClick={() => { setSortField(v); setOpenMenu(null); }}
                                    className={`w-full text-left px-3 py-1.5 rounded-lg text-xs font-semibold transition cursor-pointer ${sortField === v ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-900'}`}
                                  >
                                    {label}
                                  </button>
                                ))}
                                <div className="border-t border-slate-100 dark:border-slate-800 mt-1 pt-1 flex gap-1 px-1">
                                  <button onClick={() => { setSortDir('asc'); setOpenMenu(null); }} className={`flex-1 text-[11px] font-bold py-1 rounded cursor-pointer ${sortDir === 'asc' ? 'bg-blue-600 text-white' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-900'}`}>Asc ↑</button>
                                  <button onClick={() => { setSortDir('desc'); setOpenMenu(null); }} className={`flex-1 text-[11px] font-bold py-1 rounded cursor-pointer ${sortDir === 'desc' ? 'bg-blue-600 text-white' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-900'}`}>Desc ↓</button>
                                </div>
                              </div>
                            </>
                          )}
                        </div>

                        {/* GROUP BY */}
                        <div className="relative">
                          <button
                            onClick={() => setOpenMenu(openMenu === 'tableGroup' ? null : 'tableGroup')}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-wider rounded-lg shadow-sm cursor-pointer transition-all ${
                              groupBy !== 'pipeline'
                                ? 'border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400'
                                : 'border border-slate-200 dark:border-slate-800 bg-slate-50 hover:bg-slate-100 dark:bg-slate-900 dark:hover:bg-slate-850 text-slate-800 dark:text-slate-100'
                            }`}
                          >
                            <LayoutGrid className="w-3.5 h-3.5" /> GROUP BY
                          </button>
                          {openMenu === 'tableGroup' && (
                            <>
                              <div className="fixed inset-0 z-40" onClick={() => setOpenMenu(null)} />
                              <div className="absolute left-0 mt-2 w-48 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl z-50 p-1.5">
                                {([
                                  { v: 'pipeline', label: 'Pipeline (default)' },
                                  { v: 'status', label: 'Status' },
                                  { v: 'owner', label: 'Owner' },
                                  { v: 'service', label: 'Service' },
                                  { v: 'industry', label: 'Industry' },
                                  { v: 'lastUpdateDate', label: 'Last Update Date' },
                                ] as const).map(({ v, label }) => (
                                  <button
                                    key={v}
                                    onClick={() => { setGroupBy(v); setOpenMenu(null); }}
                                    className={`w-full text-left px-3 py-1.5 rounded-lg text-xs font-semibold transition cursor-pointer ${groupBy === v ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-900'}`}
                                  >
                                    {label}
                                  </button>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 text-slate-500 dark:text-slate-400 text-xs font-bold uppercase tracking-wider">
                            <th className="p-3 pl-6">Lead Name / Contact</th>
                            <th className="p-3">Company</th>
                            <th className="p-3">Pipeline Stage</th>
                            <th className="p-3">Status</th>
                            {canViewFinancials && <th className="p-3">Deal Value</th>}
                            <th className="p-3">Owner / Salesperson</th>
                            <th className="p-3">Location</th>
                            <th className="p-3">Service</th>
                            <th className="p-3">Industry</th>
                            <th className="p-3">Follow-up Date</th>
                            <th className="p-3 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayedLeads.length === 0 ? (
                            <tr>
                              <td colSpan={canViewFinancials ? 11 : 10} className="p-8 text-center text-xs text-slate-400 font-medium">
                                No leads found matching the filters or search query.
                              </td>
                            </tr>
                          ) : (
                            tableGroups.map((group) => (
                              <React.Fragment key={group.groupValue}>
                                {/* Group Header Row */}
                                <tr className="bg-slate-100/70 dark:bg-slate-900/60 font-bold border-b border-slate-200 dark:border-slate-800">
                                  <td colSpan={canViewFinancials ? 11 : 10} className="p-2.5 px-4 text-slate-700 dark:text-slate-350">
                                    <div className="flex items-center gap-2">
                                      <span className="text-[10px] font-black uppercase tracking-wider bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 px-2.5 py-0.5 rounded">
                                        {groupBy.toUpperCase()}: {group.groupValue}
                                      </span>
                                      <span className="text-[10px] text-slate-400 dark:text-slate-500 font-medium font-sans">({group.leads.length} leads)</span>
                                    </div>
                                  </td>
                                </tr>
                                {group.leads.map((lead) => {
                                  const sColor = resolveStatusColor(lead.status, statusColors);
                                  return (
                                    <tr key={lead.id} className="border-b border-slate-100 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-900/40 text-xs text-slate-800 dark:text-slate-200">
                                      <td className="p-3 pl-6">
                                        <div className="font-bold text-slate-900 dark:text-white">{lead.name}</div>
                                        {lead.contactPerson && <div className="text-[10px] text-slate-400">{lead.contactPerson}</div>}
                                      </td>
                                      <td className="p-3 font-semibold">{lead.company}</td>
                                      <td className="p-3">
                                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                                          {lead.pipeline}
                                        </span>
                                      </td>
                                      <td className="p-3">
                                        <span 
                                          className="px-2 py-0.5 rounded text-[10px] font-bold text-white uppercase tracking-wider"
                                          style={{ backgroundColor: sColor }}
                                        >
                                          {lead.status}
                                        </span>
                                      </td>
                                      {canViewFinancials && (
                                        <td className="p-3 font-mono font-bold text-slate-900 dark:text-white">
                                          ${lead.dealValue.toLocaleString()}
                                        </td>
                                      )}
                                      <td className="p-3 font-medium">
                                        {lead.owner || <span className="text-slate-400 italic">Unassigned</span>}
                                      </td>
                                      <td className="p-3 text-slate-500 dark:text-slate-400">{lead.location || '—'}</td>
                                      <td className="p-3 font-medium text-slate-700 dark:text-slate-300">{lead.service || '—'}</td>
                                      <td className="p-3 text-slate-500 dark:text-slate-400">{lead.industry || '—'}</td>
                                      <td className="p-3">
                                        {lead.followUpDate ? (
                                          <span className={`font-mono ${lead.followUpCompletedAt ? 'line-through text-slate-400' : 'text-blue-600 dark:text-blue-400 font-bold'}`}>
                                            {lead.followUpDate}
                                          </span>
                                        ) : '—'}
                                      </td>
                                      <td className="p-3 text-right">
                                        <div className="flex justify-end items-center gap-1.5">
                                          <button
                                            type="button"
                                            onClick={() => handleEditLead(lead)}
                                            className="p-1.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition"
                                            title="Edit Lead"
                                          >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => handleDeleteLead(lead.id)}
                                            className="p-1.5 bg-red-50 dark:bg-red-950/20 text-red-500 hover:bg-red-100 dark:hover:bg-red-950/40 rounded transition"
                                            title="Delete Lead"
                                          >
                                            <Trash2 className="w-3.5 h-3.5" />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </React.Fragment>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}


                {layoutMode === 'kanban' && (
                  /* Section Kanban Board (Kanban Mode) */
                  <div className="pb-12">
                    <KanbanBoard
                      leads={displayedLeads}
                      onUpdatePipeline={handleUpdatePipeline}
                      onDeleteLead={handleDeleteLead}
                      onEditLead={handleEditLead}
                      onAddNewLeadToBoard={handleAddNewLeadToBoard}
                      searchQuery={searchQuery}
                      statusColors={statusColors}
                      activeStageTab={activeStageTab}
                    />
                  </div>
                )}
              </>
            )}

            {/* VIEW 2: CONTACTS DIRECTORY TAB */}
            {activeTab === 'contacts' && (() => {
              const filteredLeadsByArchive = leads.filter((l) => showArchivedContacts ? l.isArchived : !l.isArchived);
              const visibleLeads = filteredLeadsByArchive.filter((l) =>
                (l.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                (l.company || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                (l.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                (l.owner && l.owner.toLowerCase().includes(searchQuery.toLowerCase())) ||
                (l.contactPerson && l.contactPerson.toLowerCase().includes(searchQuery.toLowerCase()))
              );
              const tableMinWidth = (Object.values(columnWidths) as number[]).reduce((a, b) => a + b, 0);

              return (
                <div className="bg-white dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden p-6 transition-colors">
                  <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6 border-b border-slate-100 dark:border-slate-800 pb-4">
                    <div>
                      <h2 className="text-base font-bold text-slate-800 dark:text-slate-100">Unified CRM Contact Directory</h2>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Flat directory table representing all leads with extensive field data.</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto lg:justify-end">
                      {/* Archive toggle */}
                      <button
                        onClick={() => {
                          setShowArchivedContacts(!showArchivedContacts);
                          setSelectedContactIds([]); // clear selection
                        }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition flex items-center gap-1.5 cursor-pointer ${
                          showArchivedContacts
                            ? 'bg-purple-50 border-purple-200 text-purple-700 dark:bg-purple-950/40 dark:border-purple-900 dark:text-purple-300'
                            : 'bg-white border-slate-200 text-slate-700 dark:bg-slate-900 dark:border-slate-800 dark:text-slate-300'
                        }`}
                      >
                        <Archive className="w-3.5 h-3.5" />
                        {showArchivedContacts ? 'Showing: Archived' : 'Show Archived Contacts'}
                      </button>

                      <span className="text-xs font-mono font-semibold bg-slate-100 dark:bg-slate-900 text-slate-700 dark:text-slate-300 border border-transparent dark:border-slate-800 px-3 py-1.5 rounded">
                        Total Listed: {filteredLeadsByArchive.length} Contacts
                      </span>
                    </div>
                  </div>

                  {/* Bulk Actions Bar */}
                  {selectedContactIds.length > 0 && (
                    <div className="bg-blue-50 dark:bg-blue-950/40 border-l-4 border-blue-500 p-3 mb-4 rounded-r-lg flex flex-col sm:flex-row items-center justify-between gap-4 shadow-sm animate-fade-in transition-colors">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-blue-800 dark:text-blue-250 bg-blue-100 dark:bg-blue-900/60 px-2.5 py-0.5 rounded-full">
                          {selectedContactIds.length} Selected
                        </span>
                        <p className="text-xs font-medium text-slate-700 dark:text-slate-300">
                          Apply bulk actions to selected contacts
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-3">
                        {/* Status Selector Dropdown */}
                        <div className="flex items-center gap-1.5">
                          <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase">Update Status:</span>
                          <select
                            onChange={(e) => {
                              if (e.target.value) {
                                handleBulkUpdateStatus(selectedContactIds, e.target.value);
                                setSelectedContactIds([]); // clear selection
                                e.target.value = ''; // reset dropdown
                              }
                            }}
                            className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-800 rounded px-2 py-1 text-xs font-bold text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer"
                            defaultValue=""
                          >
                            <option value="" disabled>Select Status...</option>
                            <option value="New">New</option>
                            <option value="New Lead">New Lead</option>
                            <option value="Website Lead">Website Lead</option>
                            <option value="Attempted to Contact">Attempted to Contact</option>
                            <option value="Cold Call Lead">Cold Call Lead</option>
                            <option value="Sent Quote">Sent Quote</option>
                            <option value="Sent Credit App">Sent Credit App</option>
                            <option value="In Process">In Process</option>
                            <option value="Working on it">Working on it</option>
                            <option value="Won">Won</option>
                            <option value="Complete">Complete</option>
                            <option value="Lost">Lost</option>
                            <option value="Broker">Broker</option>
                            <option value="Shipper">Shipper</option>
                            <option value="Revisit Later | ADD FU DATE">Revisit Later</option>
                          </select>
                        </div>

                        <span className="text-slate-300 dark:text-slate-800">|</span>

                        {/* Bulk Archive */}
                        <button
                          onClick={() => {
                            const updated = leads.map((l) => {
                              if (selectedContactIds.includes(l.id)) {
                                return { ...l, isArchived: !showArchivedContacts };
                              }
                              return l;
                            });
                            saveLeads(updated);
                            setSelectedContactIds([]);
                          }}
                          className="bg-purple-600 hover:bg-purple-700 text-white font-bold px-3 py-1.5 rounded text-xs transition cursor-pointer flex items-center gap-1 shadow-sm"
                        >
                          <Archive className="w-3.5 h-3.5" />
                          {showArchivedContacts ? 'Restore Selected' : 'Archive Selected'}
                        </button>

                        {/* Bulk Delete */}
                        <button
                          onClick={() => {
                            if (confirm(`Are you sure you want to delete ${selectedContactIds.length} selected contacts?`)) {
                              handleBulkDeleteLeads(selectedContactIds);
                              setSelectedContactIds([]);
                            }
                          }}
                          className="bg-red-600 hover:bg-red-700 text-white font-bold px-3 py-1.5 rounded text-xs transition cursor-pointer flex items-center gap-1 shadow-sm"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Delete Selected
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse table-fixed select-none" style={{ minWidth: `${tableMinWidth}px` }}>
                      <thead>
                        <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 text-slate-500 dark:text-slate-400 text-xs uppercase font-bold tracking-wider">
                          <th style={{ width: columnWidths.select }} className="p-3">
                            <input
                              type="checkbox"
                              checked={visibleLeads.length > 0 && visibleLeads.every(l => selectedContactIds.includes(l.id))}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedContactIds(prev => {
                                    const union = new Set([...prev, ...visibleLeads.map(l => l.id)]);
                                    return Array.from(union);
                                  });
                                } else {
                                  setSelectedContactIds(prev => prev.filter(id => !visibleLeads.some(l => l.id === id)));
                                }
                              }}
                              className="rounded border-slate-300 dark:border-slate-800 text-blue-600 focus:ring-blue-500 cursor-pointer"
                            />
                          </th>
                          <th style={{ width: columnWidths.company }} className="p-3 relative">
                            <span className="truncate block">Company & Contact</span>
                            <div
                              onMouseDown={(e) => handleResizeStart('company', e)}
                              className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-blue-500 bg-transparent active:bg-blue-600 transition-colors z-10"
                            />
                          </th>
                          <th style={{ width: columnWidths.email }} className="p-3 relative">
                            <span className="truncate block">Email & Phone</span>
                            <div
                              onMouseDown={(e) => handleResizeStart('email', e)}
                              className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-blue-500 bg-transparent active:bg-blue-600 transition-colors z-10"
                            />
                          </th>
                          <th style={{ width: columnWidths.owner }} className="p-3 relative">
                            <span className="truncate block">Owner</span>
                            <div
                              onMouseDown={(e) => handleResizeStart('owner', e)}
                              className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-blue-500 bg-transparent active:bg-blue-600 transition-colors z-10"
                            />
                          </th>
                          <th style={{ width: columnWidths.service }} className="p-3 relative">
                            <span className="truncate block">Service / Industry</span>
                            <div
                              onMouseDown={(e) => handleResizeStart('service', e)}
                              className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-blue-500 bg-transparent active:bg-blue-600 transition-colors z-10"
                            />
                          </th>
                          <th style={{ width: columnWidths.location }} className="p-3 relative">
                            <span className="truncate block">Location</span>
                            <div
                              onMouseDown={(e) => handleResizeStart('location', e)}
                              className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-blue-500 bg-transparent active:bg-blue-600 transition-colors z-10"
                            />
                          </th>
                          {canViewFinancials && (
                            <th style={{ width: columnWidths.value }} className="p-3 relative">
                              <span className="truncate block">Deal Value</span>
                              <div
                                onMouseDown={(e) => handleResizeStart('value', e)}
                                className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-blue-500 bg-transparent active:bg-blue-600 transition-colors z-10"
                              />
                            </th>
                          )}
                          <th style={{ width: columnWidths.status }} className="p-3 relative">
                            <span className="truncate block">Pipeline Status</span>
                            <div
                              onMouseDown={(e) => handleResizeStart('status', e)}
                              className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-blue-500 bg-transparent active:bg-blue-600 transition-colors z-10"
                            />
                          </th>
                          <th style={{ width: columnWidths.actions }} className="p-3 relative text-right">
                            <span className="truncate block">Actions</span>
                            <div
                              onMouseDown={(e) => handleResizeStart('actions', e)}
                              className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-blue-500 bg-transparent active:bg-blue-600 transition-colors z-10"
                            />
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleLeads.map((lead) => (
                          <tr key={lead.id} className="border-b border-slate-100 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-900/40 text-xs text-slate-800 dark:text-slate-200 group">
                            <td className="p-3">
                              <input
                                type="checkbox"
                                checked={selectedContactIds.includes(lead.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedContactIds(prev => [...prev, lead.id]);
                                  } else {
                                    setSelectedContactIds(prev => prev.filter(id => id !== lead.id));
                                  }
                                }}
                                className="rounded border-slate-300 dark:border-slate-800 text-blue-600 focus:ring-blue-500 cursor-pointer"
                              />
                            </td>
                            <td className="p-3">
                              <div className="flex flex-col truncate">
                                <span className="font-bold text-slate-800 dark:text-slate-100 text-sm truncate">{lead.name}</span>
                                <span className="text-slate-500 dark:text-slate-400 truncate">{lead.company}</span>
                              </div>
                            </td>
                            <td className="p-3">
                              <div className="flex flex-col space-y-0.5 truncate">
                                {lead.email && (
                                  <a href={`mailto:${lead.email}`} className="text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 truncate">
                                    <Mail className="w-3 h-3 opacity-60" /> {lead.email}
                                  </a>
                                )}
                                {lead.phone && <span className="text-slate-500 dark:text-slate-400 truncate">{lead.phone}</span>}
                              </div>
                            </td>
                            <td className="p-3 font-semibold text-slate-700 dark:text-slate-300 truncate">{lead.owner || '—'}</td>
                            <td className="p-3">
                              <div className="flex flex-col truncate">
                                <span className="font-bold text-slate-700 dark:text-slate-300 truncate">{lead.service || '—'}</span>
                                <span className="text-[10px] text-slate-400 dark:text-slate-500 font-mono truncate">{lead.industry || '—'}</span>
                              </div>
                            </td>
                            <td className="p-3 text-slate-600 dark:text-slate-400 font-medium truncate">{lead.location || '—'}</td>
                            {canViewFinancials && (
                              <td className="p-3 font-mono font-bold text-slate-700 dark:text-slate-300 text-sm truncate">
                                ${lead.dealValue.toLocaleString()}
                              </td>
                            )}
                            <td className="p-3">
                              <div className="flex flex-col gap-1 truncate">
                                <span className="text-[10px] bg-slate-100 dark:bg-slate-900 text-slate-700 dark:text-slate-300 px-2 py-0.5 rounded border border-slate-200/50 dark:border-slate-800 self-start truncate">
                                  {lead.pipeline}
                                </span>
                                <span className="text-[9px] font-bold uppercase text-blue-600 dark:text-blue-400 truncate">
                                  Status: {lead.status}
                                </span>
                              </div>
                            </td>
                            <td className="p-3 text-right">
                              <div className="flex justify-end items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                                {/* Quick Edit */}
                                <button
                                  onClick={() => setQuickEditingLead(lead)}
                                  className="p-1 bg-amber-50 hover:bg-amber-100 dark:bg-amber-950/40 dark:hover:bg-amber-900/40 text-amber-600 dark:text-amber-400 rounded transition cursor-pointer flex items-center gap-1 text-[10px] font-bold px-1.5"
                                  title="Quick Edit"
                                >
                                  <Sparkles className="w-3.5 h-3.5" /> Quick Edit
                                </button>
                                {/* Archive / Restore */}
                                <button
                                  onClick={() => handleToggleArchiveLead(lead.id)}
                                  className={`p-1 rounded transition cursor-pointer flex items-center gap-1 text-[10px] font-bold px-1.5 ${
                                    lead.isArchived
                                      ? 'bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:hover:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400'
                                      : 'bg-purple-50 hover:bg-purple-100 dark:bg-purple-950/40 dark:hover:bg-purple-900/40 text-purple-600 dark:text-purple-400'
                                  }`}
                                  title={lead.isArchived ? "Restore Lead" : "Archive Lead"}
                                >
                                  <Archive className="w-3.5 h-3.5" />
                                  {lead.isArchived ? 'Restore' : 'Archive'}
                                </button>

                                <span className="text-slate-300 dark:text-slate-800">|</span>

                                <button
                                  onClick={() => handleEditLead(lead)}
                                  className="p-1 hover:bg-blue-50 dark:hover:bg-blue-950/40 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 rounded transition cursor-pointer"
                                  title="Edit Lead"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-pencil"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                                </button>
                                <button
                                  onClick={() => handleDeleteLead(lead.id)}
                                  className="p-1 hover:bg-red-50 dark:hover:bg-red-950/40 text-red-500 hover:text-red-700 dark:hover:text-red-400 rounded transition cursor-pointer"
                                  title="Delete Lead"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}

            {/* VIEW 3: ANALYTICS VISUALIZATIONS */}
            {activeTab === 'analytics' && (
              <div className="space-y-8 pb-12">
                <div className="bg-white dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm p-6 transition-colors">
                  <h2 className="text-base font-bold text-slate-800 dark:text-slate-100">Pipeline Performance Analytics</h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Visual breakdowns of deals, conversion distributions, and pipeline allocation.</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Status Breakdown Bar Chart */}
                  <div className="bg-white dark:bg-slate-950 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col transition-colors">
                    <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-4">Deal Count by Status</h3>
                    
                    <div className="space-y-4 flex-1 flex flex-col justify-center">
                      {[
                        { label: 'Won / Complete (Onboarded)', count: leads.filter(l => l.status === 'Won' || l.status === 'Complete').length, color: 'bg-emerald-500' },
                        { label: 'Working on it / In Process', count: leads.filter(l => l.status === 'Working on it' || l.status === 'In Process').length, color: 'bg-amber-500' },
                        { label: 'New / Website Opportunity', count: leads.filter(l => l.status === 'New' || l.status === 'New Lead' || l.status === 'Website Lead').length, color: 'bg-blue-400' },
                        { label: 'Attempted to Contact', count: leads.filter(l => l.status === 'Attempted to Contact').length, color: 'bg-purple-500' },
                        { label: 'Lost / Not Awarded', count: leads.filter(l => l.status === 'Lost' || l.status === 'Not Awarded').length, color: 'bg-rose-500' },
                      ].map((item, index) => {
                        const pct = leads.length > 0 ? (item.count / leads.length) * 100 : 0;
                        return (
                          <div key={index} className="space-y-1">
                            <div className="flex justify-between text-xs font-medium text-slate-600 dark:text-slate-400">
                              <span>{item.label}</span>
                              <span className="font-bold text-slate-800 dark:text-slate-200">{item.count} leads ({pct.toFixed(0)}%)</span>
                            </div>
                            <div className="h-3 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                              <div
                                className={`h-full ${item.color} rounded-full transition-all duration-500`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Estimated Value or Volume Distribution */}
                  <div className="bg-white dark:bg-slate-950 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col transition-colors">
                    <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-4">
                      {canViewFinancials ? 'Pipeline Value Distribution' : 'Pipeline Volume Distribution'}
                    </h3>
                    
                    <div className="space-y-4 flex-1 flex flex-col justify-center">
                      {[
                        {
                          label: 'Website Leads',
                          value: leads.filter(l => l.pipeline === 'Website Leads').reduce((acc, l) => acc + (canViewFinancials ? l.dealValue : 1), 0),
                          count: leads.filter(l => l.pipeline === 'Website Leads').length,
                          color: 'bg-blue-500',
                        },
                        {
                          label: 'New & Cold Call Leads',
                          value: leads.filter(l => l.pipeline === 'New Leads' || l.pipeline === 'Cold Call Leads').reduce((acc, l) => acc + (canViewFinancials ? l.dealValue : 1), 0),
                          count: leads.filter(l => l.pipeline === 'New Leads' || l.pipeline === 'Cold Call Leads').length,
                          color: 'bg-amber-500',
                        },
                        {
                          label: 'Quoted Leads',
                          value: leads.filter(l => l.pipeline === 'Quoted').reduce((acc, l) => acc + (canViewFinancials ? l.dealValue : 1), 0),
                          count: leads.filter(l => l.pipeline === 'Quoted').length,
                          color: 'bg-cyan-500',
                        },
                        {
                          label: 'Complete & Active Deliveries',
                          value: leads.filter(l => l.pipeline === 'Complete' || l.pipeline === 'In Process' || l.pipeline === 'Awaiting First Shipment').reduce((acc, l) => acc + (canViewFinancials ? l.dealValue : 1), 0),
                          count: leads.filter(l => l.pipeline === 'Complete' || l.pipeline === 'In Process' || l.pipeline === 'Awaiting First Shipment').length,
                          color: 'bg-emerald-500',
                        },
                      ].map((item, index) => {
                        const totalBase = canViewFinancials ? totalVal : leads.length;
                        const pct = totalBase > 0 ? (item.value / totalBase) * 100 : 0;
                        return (
                          <div key={index} className="space-y-1">
                            <div className="flex justify-between text-xs font-medium text-slate-600 dark:text-slate-400">
                              <span>{item.label}</span>
                              <span className="font-bold text-slate-800 dark:text-slate-200">
                                {canViewFinancials ? `$${item.value.toLocaleString()}` : `${item.count} leads`} ({pct.toFixed(0)}%)
                              </span>
                            </div>
                            <div className="h-3 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                              <div
                                className={`h-full ${item.color} rounded-full transition-all duration-500`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Additional Quick Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-white dark:bg-slate-950 p-5 rounded-xl border border-slate-200 dark:border-slate-800 text-center shadow-sm transition-colors">
                    <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider font-semibold">
                      {canViewFinancials ? 'Avg Lead Value' : 'Total Pipeline Leads'}
                    </p>
                    <p className="text-2xl font-black text-slate-800 dark:text-slate-100 mt-1">
                      {canViewFinancials 
                        ? `$${leads.length > 0 ? Math.round(totalVal / leads.length).toLocaleString() : '0'}`
                        : leads.length.toLocaleString()
                      }
                    </p>
                  </div>
                  <div className="bg-white dark:bg-slate-950 p-5 rounded-xl border border-slate-200 dark:border-slate-800 text-center shadow-sm transition-colors">
                    <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider font-semibold">
                      {canViewFinancials ? 'Won Pipeline' : 'Won Leads'}
                    </p>
                    <p className="text-2xl font-black text-emerald-600 dark:text-emerald-400 mt-1">
                      {canViewFinancials
                        ? `$${wonLeads.reduce((acc, l) => acc + l.dealValue, 0).toLocaleString()}`
                        : wonLeads.length.toLocaleString()
                      }
                    </p>
                  </div>
                  <div className="bg-white dark:bg-slate-950 p-5 rounded-xl border border-slate-200 dark:border-slate-800 text-center shadow-sm transition-colors">
                    <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider font-semibold">
                      {canViewFinancials ? 'In Process Pipeline' : 'In Process Leads'}
                    </p>
                    <p className="text-2xl font-black text-amber-600 dark:text-amber-400 mt-1">
                      {canViewFinancials
                        ? `$${workingLeads.reduce((acc, l) => acc + l.dealValue, 0).toLocaleString()}`
                        : workingLeads.length.toLocaleString()
                      }
                    </p>
                  </div>
                  <div className="bg-white dark:bg-slate-950 p-5 rounded-xl border border-slate-200 dark:border-slate-800 text-center shadow-sm transition-colors">
                    <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider font-semibold">
                      {canViewFinancials ? 'Closed Lost' : 'Closed Lost Leads'}
                    </p>
                    <p className="text-2xl font-black text-rose-600 dark:text-rose-400 mt-1">
                      {canViewFinancials
                        ? `$${lostLeads.reduce((acc, l) => acc + l.dealValue, 0).toLocaleString()}`
                        : lostLeads.length.toLocaleString()
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* VIEW 4: GOOGLE WORKSPACE HUB */}
            {activeTab === 'google-hub' && (
              <GoogleHub
                accessToken={accessToken}
                onConnectGoogle={handleConnectGoogle}
                onDisconnectGoogle={handleDisconnectGoogle}
                leads={leads}
                currentUserEmail={currentUser?.email || 'msterling@armellini.com'}
                onLeadReply={handleLeadEmailReply}
                userSignature={dbUser?.signature}
              />
            )}

            {/* VIEW 5: SYSTEM MAINTENANCE (Status Colors, Lead Form, Manage Industries, Template Management) */}
            {activeTab === 'settings' && (userRole === 'admin' || userRole === 'editor') && (
              <Settings
                statusColors={statusColors}
                onSaveStatusColors={handleSaveStatusColors}
                users={crmUsersList}
                onAddUser={handleAddUser}
                onUpdateRole={handleUpdateUserRole}
                onDeleteUser={handleDeleteUser}
                accessToken={accessToken}
              />
            )}

            {/* VIEW 5.5: ADMIN SETTINGS (User Access Control & Workspace Directory Import) */}
            {activeTab === 'admin-settings' && userRole === 'admin' && (
              <AdminSettings
                users={crmUsersList}
                onAddUser={handleAddUser}
                onUpdateRole={handleUpdateUserRole}
                onDeleteUser={handleDeleteUser}
                onImpersonateUser={handleStartImpersonation}
                currentUserEmail={currentUser?.email}
                accessToken={accessToken}
                announcements={announcements}
                onSaveAnnouncement={handleSaveAnnouncement}
                onDeleteAnnouncement={handleDeleteAnnouncement}
                onToggleActiveAnnouncement={handleToggleActiveAnnouncement}
                onRefreshAnnouncements={loadAnnouncements}
              />
            )}

            {/* VIEW 6: NON-FLORAL LOGISTICS MODULE */}
            {activeTab === 'non-floral' && (
              <NonFloral
                currentUserEmail={currentUser?.email || 'msterling@armellini.com'}
                subTab={nonFloralTab}
                onSubTabChange={setNonFloralTab}
                accessToken={accessToken}
                users={crmUsersList}
                userRole={userRole}
              />
            )}

            {/* VIEW 7: CONSIGNEE & SHIPPER DIRECTORY */}
            {(activeTab === 'cdata-search' || activeTab === 'customer-master' || activeTab === 'consignee-shipper' || activeTab === 'directory') && (
              <ConsigneeShipperSearch onQuoteLead={handleQuoteLead} leads={leads} userRole={userRole} />
            )}

            {/* VIEW 7.5: ARMELLINI AELIS 4 LOGISTICS HUB */}
            {activeTab === 'aelis-logistics' && (
              <AelisLogisticsHub
                leads={leads}
                userRole={userRole}
                getIdToken={async () => {
                  try {
                    return currentUser ? await currentUser.getIdToken() : null;
                  } catch {
                    return null;
                  }
                }}
              />
            )}


            {/* VIEW 8: CLIENT FORMS (forms.armellini.com management module) */}
            {activeTab === 'client-forms' && (
              <FormsHub
                subTab={formsSubTab}
                onSubTabChange={setFormsSubTab}
                onNotify={addNotification}
                leads={leads}
              />
            )}

            {/* VIEW 9: HELP CENTER */}
            {(activeTab === 'help-center' || activeTab === 'help') && (
              <HelpCenter />
            )}

            {/* VIEW 10: USER PROFILE & SIGNATURE */}
            {activeTab === 'profile' && (
              <Profile
                userEmail={currentUser?.email || 'msterling@armellini.com'}
                onProfileUpdate={(updatedUser) => setDbUser(updatedUser)}
              />
            )}

          </div>
        </div>
      </main>

      {/* 3. ADD NEW LEAD / EDIT MODAL SLIDE-OVER */}
      <SlideOver
        isOpen={isSlideOverOpen}
        onClose={() => {
          setIsSlideOverOpen(false);
          setEditingLead(null);
          setDefaultPipeline(null);
        }}
        onSave={handleSaveLead}
        editingLead={editingLead}
        defaultPipeline={defaultPipeline}
        statusColors={statusColors}
        onSaveStatusColors={handleSaveStatusColors}
        users={crmUsersList}
        showToast={(msg, toastLeadId) => addNotification('System', msg, toastLeadId || editingLead?.id)}
        accessToken={accessToken}
        userRole={userRole}
      />

      {/* AI Advisor Modal */}
      <AIAdvisorModal
        isOpen={isAIAdvisorOpen}
        onClose={() => setIsAIAdvisorOpen(false)}
        leads={leads}
        onUpdateStatus={handleUpdateStatus}
        statusColors={statusColors}
      />


      {/* 4. BULK EMAIL COMPOSE MODAL OVERLAY */}
      {bulkEmailModalOpen && (
        <div className="fixed inset-0 z-[100] overflow-hidden flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            onClick={() => {
              if (!bulkEmailSending) setBulkEmailModalOpen(false);
            }}
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
          />

          {/* Modal Card Panel */}
          <div className="bg-white dark:bg-slate-950 w-full max-w-xl rounded-xl shadow-2xl border border-slate-200 dark:border-slate-800 z-50 overflow-hidden relative flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-blue-100 dark:bg-blue-950 text-blue-600 dark:text-blue-400 rounded-lg">
                  <Mail className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">Bulk Email Campaign Manager</h3>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">Compose and dispatch templates to {bulkEmailRecipients.length} selected lead contacts.</p>
                </div>
              </div>
              {!bulkEmailSending && (
                <button
                  type="button"
                  onClick={() => setBulkEmailModalOpen(false)}
                  className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-800 rounded transition cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Success Animation Screen */}
            {bulkEmailSuccess ? (
              <div className="p-12 flex flex-col items-center justify-center text-center gap-4 flex-1">
                <div className="w-14 h-14 bg-emerald-100 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center animate-bounce">
                  <Check className="w-7 h-7 stroke-[3px]" />
                </div>
                <div>
                  <h4 className="text-base font-bold text-slate-800 dark:text-slate-100">Campaign Dispatched Successfully!</h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-sm mx-auto">
                    All {bulkEmailRecipients.length} bulk emails were prepared, dispatched, and logged into each customer's CRM activity history.
                  </p>
                </div>
              </div>
            ) : bulkEmailSending ? (
              /* Sending/Loading Screen */
              <div className="p-12 flex flex-col items-center justify-center gap-5 flex-1">
                <div className="relative w-16 h-16 flex items-center justify-center">
                  <div className="absolute inset-0 rounded-full border-4 border-slate-200 dark:border-slate-800" />
                  <div className="absolute inset-0 rounded-full border-4 border-t-blue-500 animate-spin" />
                  <span className="text-xs font-mono font-bold text-blue-600 dark:text-blue-400">{bulkEmailProgress}%</span>
                </div>
                <div className="text-center">
                  <h4 className="text-xs font-bold text-slate-800 dark:text-slate-100 uppercase tracking-widest">Processing Freight Campaigns</h4>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">Please hold as we configure payloads for {bulkEmailRecipients.length} recipients...</p>
                </div>
                {/* Progress bar */}
                <div className="w-full max-w-xs h-2 bg-slate-100 dark:bg-slate-900 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 transition-all duration-155" style={{ width: `${bulkEmailProgress}%` }} />
                </div>
              </div>
            ) : (
              /* Compose Form Screen */
              <div className="p-6 space-y-4 overflow-y-auto flex-1">
                {/* Recipients list block */}
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                    Recipients ({bulkEmailRecipients.length})
                  </label>
                  <div className="border border-slate-200 dark:border-slate-850 bg-slate-50/50 dark:bg-slate-900/30 rounded-lg p-2.5 max-h-[70px] overflow-y-auto flex flex-wrap gap-1.5">
                    {bulkEmailRecipients.map((rec, i) => (
                      <span key={i} className="text-[10px] font-mono font-semibold bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded border border-blue-100 dark:border-blue-900/40">
                        {rec}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Template picker */}
                <div>
                  <label htmlFor="bulk-template" className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                    Select Logistics Campaign Template
                  </label>
                  <select
                    id="bulk-template"
                    value={bulkEmailTemplate}
                    onChange={(e) => handleBulkEmailTemplateChange(e.target.value)}
                    className="block w-full rounded-md border border-slate-300 dark:border-slate-850 bg-white dark:bg-slate-950 px-3 py-1.5 text-xs text-slate-800 dark:text-slate-100 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition cursor-pointer"
                  >
                    <option value="outreach">Outreach Introduction (Temperature-Controlled Truckload)</option>
                    <option value="quote">Rate Quote Invitation (Lanes & Lanes Analysis)</option>
                    <option value="credit">Credit Application referral (Setup direct billing)</option>
                  </select>
                </div>

                {/* Subject */}
                <div>
                  <label htmlFor="bulk-subject" className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                    Email Subject Header
                  </label>
                  <input
                    type="text"
                    id="bulk-subject"
                    value={bulkEmailSubject}
                    onChange={(e) => setBulkEmailSubject(e.target.value)}
                    placeholder="Subject heading..."
                    className="block w-full rounded-md border border-slate-300 dark:border-slate-850 bg-white dark:bg-slate-950 px-3 py-1.5 text-xs text-slate-800 dark:text-slate-100 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition placeholder:text-slate-400"
                  />
                </div>

                {/* Body Content */}
                <div>
                  <label htmlFor="bulk-body" className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                    Draft Message Body
                  </label>
                  <textarea
                    id="bulk-body"
                    value={bulkEmailBody}
                    onChange={(e) => setBulkEmailBody(e.target.value)}
                    rows={7}
                    placeholder="Message body content..."
                    className="block w-full rounded-md border border-slate-300 dark:border-slate-850 bg-white dark:bg-slate-950 px-3 py-2 text-xs text-slate-800 dark:text-slate-200 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition placeholder:text-slate-400 font-sans leading-relaxed"
                  />
                </div>
              </div>
            )}

            {/* Footer buttons */}
            {!bulkEmailSuccess && !bulkEmailSending && (
              <div className="px-6 py-3.5 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-850 flex items-center justify-between shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    const payload = `To: ${bulkEmailRecipients.join('; ')}\nSubject: ${bulkEmailSubject}\n\n${bulkEmailBody}`;
                    navigator.clipboard.writeText(payload);
                    alert('Recipients, Subject, and Body copied to clipboard!');
                  }}
                  className="text-xs font-bold text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
                >
                  Copy Mail Merge Payload
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setBulkEmailModalOpen(false)}
                    className="rounded-md border border-slate-300 dark:border-slate-800 bg-white dark:bg-slate-950 px-3.5 py-1.5 text-xs font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900 cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSendBulkEmails}
                    className="inline-flex justify-center items-center gap-1.5 rounded-md border border-transparent bg-blue-600 px-3.5 py-1.5 text-xs font-bold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition cursor-pointer"
                  >
                    <Send className="w-3 h-3" /> Send to {bulkEmailRecipients.length} Contacts
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* 5. QUICK EDIT MODAL OVERLAY */}
      {quickEditingLead && (
        <div className="fixed inset-0 z-[100] overflow-hidden flex items-center justify-center p-4 animate-fade-in">
          {/* Backdrop */}
          <div
            onClick={() => setQuickEditingLead(null)}
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
          />

          {/* Modal Card Panel */}
          <div className="bg-white dark:bg-slate-950 w-full max-w-lg rounded-xl shadow-2xl border border-slate-200 dark:border-slate-800 z-50 overflow-hidden relative flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-amber-100 dark:bg-amber-950 text-amber-600 dark:text-amber-400 rounded-lg">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">Quick Edit Lead</h3>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">Quickly update key details for {quickEditingLead.company}.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setQuickEditingLead(null)}
                className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-800 rounded transition cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Quick Edit Form Body */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const name = formData.get('name') as string;
                const company = formData.get('company') as string;
                const email = formData.get('email') as string;
                const phone = formData.get('phone') as string;
                const status = formData.get('status') as string;
                const dealValue = parseFloat(formData.get('dealValue') as string) || 0;

                const updatedLeads = leads.map((l) => {
                  if (l.id === quickEditingLead.id) {
                    const timestamp = new Date().toISOString();
                    const newLog: ActivityLogEntry = {
                      id: `log-${Date.now()}`,
                      timestamp,
                      action: 'Quick Edit',
                      details: `Quick updated fields`,
                      user: currentUser?.email || 'msterling@armellini.com',
                    };
                    return {
                      ...l,
                      name,
                      company,
                      email,
                      phone,
                      status,
                      dealValue,
                      lastContacted: 'Quick edited just now',
                      activityLog: [newLog, ...(l.activityLog || [])],
                    };
                  }
                  return l;
                });
                saveLeads(updatedLeads);
                // Save updated lead to firestore as well
                const fullUpdatedLead = updatedLeads.find(l => l.id === quickEditingLead.id);
                if (fullUpdatedLead) {
                  persistLead(fullUpdatedLead);
                }
                setQuickEditingLead(null);
              }}
              className="p-6 space-y-4 overflow-y-auto"
            >
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">Lead Name</label>
                  <input
                    type="text"
                    name="name"
                    defaultValue={quickEditingLead.name}
                    required
                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-3 py-2 text-xs font-semibold text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">Company</label>
                  <input
                    type="text"
                    name="company"
                    defaultValue={quickEditingLead.company}
                    required
                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-3 py-2 text-xs font-semibold text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">Email</label>
                  <input
                    type="email"
                    name="email"
                    defaultValue={quickEditingLead.email}
                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-3 py-2 text-xs font-semibold text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">Phone</label>
                  <input
                    type="text"
                    name="phone"
                    defaultValue={quickEditingLead.phone || ''}
                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-3 py-2 text-xs font-semibold text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">Status</label>
                  <select
                    name="status"
                    defaultValue={quickEditingLead.status}
                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-3 py-2 text-xs font-semibold text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer"
                  >
                    <option value="Lead">Lead</option>
                    <option value="Quoted">Quoted</option>
                    <option value="Approved">Approved</option>
                    <option value="Finalizing">Finalizing</option>
                    <option value="Complete">Complete</option>
                    <option value="Transferred">Transferred</option>
                    <option value="Declined">Declined</option>
                    <option value="Not Awarded">Not Awarded</option>
                    <option value="30-Day Unresponsive">30-Day Unresponsive</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">Deal Value ($)</label>
                  <input
                    type="number"
                    name="dealValue"
                    defaultValue={quickEditingLead.dealValue}
                    step="any"
                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-3 py-2 text-xs font-semibold text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Actions Footer */}
              <div className="pt-4 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setQuickEditingLead(null)}
                  className="px-4 py-2 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900 transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-bold transition cursor-pointer"
                >
                  Quick Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* FRS Customization Modals */}
      <ColumnCustomizerModal
        isOpen={isColumnCustomizerOpen}
        onClose={() => setIsColumnCustomizerOpen(false)}
        userEmail={currentUser?.email || ''}
        initialColumns={columnPreferences}
        onSavePreferences={(cols) => {
          setColumnPreferences(cols);
          saveUserPreferences({ columns: cols });
        }}
      />

      <AdvancedFilterModal
        isOpen={isAdvancedFilterOpen}
        onClose={() => setIsAdvancedFilterOpen(false)}
        rules={advancedRules}
        onApplyRules={(rules) => {
          setAdvancedRules(rules);
          saveUserPreferences({ filters: rules });
        }}
      />

      <MultiSortModal
        isOpen={isMultiSortOpen}
        onClose={() => setIsMultiSortOpen(false)}
        sortRules={multiSortRules}
        onApplySort={(rules) => {
          setMultiSortRules(rules);
          saveUserPreferences({ sort: rules });
        }}
      />

      <ArchiveRecoveryModal
        isOpen={isArchiveModalOpen}
        onClose={() => setIsArchiveModalOpen(false)}
        archivedLeads={leads.filter(l => l.isArchived)}
        onRestoreLead={handleToggleArchiveLead}
        onPermanentDeleteLead={handleDeleteLead}
      />

      <ReauthModal
        isOpen={isReauthModalOpen}
        onClose={() => setIsReauthModalOpen(false)}
        onSuccess={(newToken) => {
          setAccessToken(newToken);
          localStorage.setItem('google_access_token', newToken);
          localStorage.setItem('google_access_token_timestamp', Date.now().toString());
          addNotification('Google session restored', 'Google Workspace access token refreshed successfully.');
        }}
        currentUserEmail={currentUser?.email}
      />

      {/* What's New & Feature Showcase Spotlight Modal */}
      <FeatureShowcaseModal
        isOpen={isWhatNewModalOpen}
        onClose={() => setIsWhatNewModalOpen(false)}
        announcement={selectedSpotlightAnnouncement || showcaseAnnouncements[0] || announcements[0] || null}
        allAnnouncements={announcements.length > 0 ? announcements : []}
        onDismissAnnouncement={handleDismissModal}
        onNavigateTab={(tab) => setActiveTab(tab)}
      />

      {/* Armellini MCP AI Data Agent Chat Widget */}
      <LogisticsChatWidget
        leads={leads}
        onUpdateStatus={handleUpdateStatus}
        statusColors={statusColors}
      />
      </div>
    </div>
  );
}
